import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  TrendingUp, Upload, Filter, ChevronLeft, ChevronRight, 
  Info, BarChart3, Briefcase, Settings, LogOut, CheckCircle2,
  Camera, FileSpreadsheet, Loader2, AlertCircle, Brain, X, Image, Shield, HelpCircle,
  Plus, ChevronDown, PenLine, Search, Trash2, Crop, Check, FileDown, AlertTriangle, Target, Calendar
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';
import { PremiumUpgradeModal } from '../components/PremiumUpgradeModal';
import { PDFExportModal } from '../components/PDFExportModal';
import brokerSymbolData from '../data/broker_symbols_compact.json';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Sidebar from '../components/Sidebar';
import TradingDayWidget from '../components/TradingDayWidget';

// Extended list of supported brokers (including JSON data brokers) - Sorted alphabetically
const BROKERS = [
  // From JSON data (with symbol autocomplete support)
  { id: 'alpaca', name: 'Alpaca', country: 'US' },
  { id: 'apex', name: 'Apex Trader Funding', country: 'US (Prop Firm)' },
  { id: 'Binance', name: 'Binance', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'Binance Futures', name: 'Binance Futures', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'Bybit', name: 'Bybit', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'Bybit Futures', name: 'Bybit Futures', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'charles_schwab', name: 'Charles Schwab', country: 'US' },
  { id: 'cmc_markets', name: 'CMC Markets', country: 'Global' },
  { id: 'CME Group', name: 'CME Group', country: 'US (Futures)', hasSymbols: true },
  { id: 'Coinbase', name: 'Coinbase', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'comdirect', name: 'Comdirect', country: 'DE' },
  { id: 'consorsbank', name: 'Consorsbank', country: 'DE' },
  { id: 'degiro', name: 'DEGIRO', country: 'EU' },
  { id: 'etrade', name: 'E*TRADE', country: 'US' },
  { id: 'etoro', name: 'eToro', country: 'Global' },
  { id: 'Eurex', name: 'Eurex', country: 'EU (Derivatives)', hasSymbols: true },
  { id: 'fidelity', name: 'Fidelity', country: 'US' },
  { id: 'flatex', name: 'Flatex', country: 'DE/AT' },
  { id: 'forex_com', name: 'FOREX.com', country: 'Global' },
  { id: 'ftmo', name: 'FTMO', country: 'EU (Prop Firm)' },
  { id: 'ig_markets', name: 'IG Markets', country: 'Global' },
  { id: 'ing_diba', name: 'ING DiBa', country: 'DE' },
  { id: 'interactive_brokers', name: 'Interactive Brokers', country: 'US' },
  { id: 'Interactive Brokers CFD', name: 'Interactive Brokers CFD', country: 'Global', hasSymbols: true },
  { id: 'kraken', name: 'Kraken', country: 'Global (Crypto)' },
  { id: 'metatrader4', name: 'MetaTrader 4 (MT4)', country: 'Global' },
  { id: 'metatrader5', name: 'MetaTrader 5 (MT5)', country: 'Global' },
  { id: 'ninjatrader', name: 'NinjaTrader', country: 'US' },
  { id: 'oanda', name: 'OANDA', country: 'Global' },
  { id: 'Oanda Forex', name: 'Oanda Forex', country: 'Global (Forex)', hasSymbols: true },
  { id: 'OKX Futures', name: 'OKX Futures', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'OKX Spot', name: 'OKX Spot', country: 'Global (Crypto)', hasSymbols: true },
  { id: 'pepperstone', name: 'Pepperstone', country: 'Global' },
  { id: 'plus500', name: 'Plus500', country: 'Global' },
  { id: 'robinhood', name: 'Robinhood', country: 'US' },
  { id: 'saxo_bank', name: 'Saxo Bank', country: 'EU' },
  { id: 'scalable_capital', name: 'Scalable Capital', country: 'DE' },
  { id: 'smartbroker', name: 'Smartbroker', country: 'DE' },
  { id: 'swissquote', name: 'Swissquote', country: 'CH' },
  { id: 'tastytrade', name: 'Tastytrade', country: 'US' },
  { id: 'td_ameritrade', name: 'TD Ameritrade', country: 'US' },
  { id: 'the5ers', name: 'The5%ers', country: 'Global (Prop Firm)' },
  { id: 'topstep', name: 'Topstep', country: 'US (Prop Firm)' },
  { id: 'trade_republic', name: 'Trade Republic', country: 'DE' },
  { id: 'tradestation', name: 'TradeStation', country: 'US' },
  { id: 'webull', name: 'Webull', country: 'US' },
  { id: 'xtb', name: 'XTB', country: 'EU' },
  // "Other" always at the end
  { id: 'other', name: 'Sonstiger Broker', country: '' },
];

import API from '../utils/api';

const DashboardPage = () => {
  const { t, getMonth, language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currency, setCurrency] = useState('USD');
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState(null);
  const [filter, setFilter] = useState('all');
  const [trades, setTrades] = useState([]);
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [stats, setStats] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [monthJournals, setMonthJournals] = useState({});
  
  // Modals
  const [showImportModal, setShowImportModal] = useState(false);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCsvMappingModal, setShowCsvMappingModal] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);
  const [showAddTradeModal, setShowAddTradeModal] = useState(false);
  const [preselectedTradeDate, setPreselectedTradeDate] = useState(null); // ISO date string from calendar day click
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProUpgradeModal, setShowProUpgradeModal] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  
  // Account creation state
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountBroker, setNewAccountBroker] = useState('');
  const [newAccountCurrency, setNewAccountCurrency] = useState('USD');
  const [accountCreationLoading, setAccountCreationLoading] = useState(false);
  const [accountError, setAccountError] = useState('');
  
  // Account deletion state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // PDF Export state
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Symbol autocomplete state
  const [symbolSuggestions, setSymbolSuggestions] = useState([]);
  const [showSymbolSuggestions, setShowSymbolSuggestions] = useState(false);
  
  // Import state
  const [importResult, setImportResult] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [csvMapping, setCsvMapping] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  
  // Screenshot state
  const [screenshotImage, setScreenshotImage] = useState(null);
  const [screenshotBase64, setScreenshotBase64] = useState('');
  const [screenshotAnalysis, setScreenshotAnalysis] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [crop, setCrop] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [showCropMode, setShowCropMode] = useState(false);
  
  // Day journal state
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayJournal, setDayJournal] = useState(null);
  const [emotionEmoji, setEmotionEmoji] = useState('');
  const [emotionNote, setEmotionNote] = useState('');
  const [dayNotes, setDayNotes] = useState('');
  const [dayScreenshots, setDayScreenshots] = useState([]);
  
  // Check-in Slider State (UI only - saved as structured note for KI Mentor)
  const [checkInData, setCheckInData] = useState({
    mood: 5,
    energy: 5,
    stress: 5,
    sleepQuality: 'okay' // 'good', 'okay', 'bad'
  });
  
  // Manual trade entry state
  const [manualTrade, setManualTrade] = useState({
    symbol: '', buy_price: '', sell_price: '', qty: '', 
    bought_timestamp: '', sold_timestamp: '', notes: '', strategy_id: '',
    direction: 'long'  // Add direction field
  });
  
  // Auto-format date input (DD.MM.YYYY)
  const formatDateInput = (value, prevValue) => {
    // Remove non-numeric characters except dots
    let cleaned = value.replace(/[^\d.]/g, '');
    
    // If user deleted a dot, allow it
    if (prevValue && prevValue.length > cleaned.length && prevValue.includes('.')) {
      return cleaned;
    }
    
    // Remove all dots to work with pure numbers
    let numbers = cleaned.replace(/\./g, '');
    
    // Auto-add dots at correct positions (DD.MM.YYYY)
    if (numbers.length >= 2) {
      numbers = numbers.slice(0, 2) + '.' + numbers.slice(2);
    }
    if (numbers.length >= 5) {
      numbers = numbers.slice(0, 5) + '.' + numbers.slice(5);
    }
    
    // Limit to DD.MM.YYYY (10 chars)
    return numbers.slice(0, 10);
  };
  
  // Auto-format time input (HH:MM:SS)
  const formatTimeInput = (value, prevValue) => {
    // Remove non-numeric characters except colons
    let cleaned = value.replace(/[^\d:]/g, '');
    
    // If user deleted a colon, allow it
    if (prevValue && prevValue.length > cleaned.length && prevValue.includes(':')) {
      return cleaned;
    }
    
    // Remove all colons to work with pure numbers
    let numbers = cleaned.replace(/:/g, '');
    
    // Auto-add colons at correct positions (HH:MM:SS)
    if (numbers.length >= 2) {
      numbers = numbers.slice(0, 2) + ':' + numbers.slice(2);
    }
    if (numbers.length >= 5) {
      numbers = numbers.slice(0, 5) + ':' + numbers.slice(5);
    }
    
    // Limit to HH:MM:SS (8 chars)
    return numbers.slice(0, 8);
  };
  
  // Handle datetime input with auto-formatting
  const handleDateTimeInput = (field, value) => {
    const prevValue = manualTrade[field] || '';
    
    // Allow backspace/delete
    if (value.length < prevValue.length) {
      setManualTrade({...manualTrade, [field]: value});
      return;
    }
    
    // Remove all non-numeric characters except space
    let cleaned = value.replace(/[^\d\s]/g, '');
    
    // Split into parts by space
    let parts = cleaned.split(/\s+/);
    let datePart = parts[0] || '';
    let timePart = parts[1] || '';
    
    // Format date: auto-add dots after DD and MM
    if (datePart.length > 0) {
      let dateNumbers = datePart.replace(/\D/g, '');
      let formattedDate = '';
      
      for (let i = 0; i < dateNumbers.length && i < 8; i++) {
        formattedDate += dateNumbers[i];
        if (i === 1 || i === 3) {
          formattedDate += '.';
        }
      }
      datePart = formattedDate;
    }
    
    // Format time: auto-add colons after HH and MM
    if (timePart.length > 0) {
      let timeNumbers = timePart.replace(/\D/g, '');
      let formattedTime = '';
      
      for (let i = 0; i < timeNumbers.length && i < 6; i++) {
        formattedTime += timeNumbers[i];
        if (i === 1 || i === 3) {
          formattedTime += ':';
        }
      }
      timePart = formattedTime;
    }
    
    // Combine with space if we have both parts
    let result = datePart;
    if (timePart) {
      result = datePart + ' ' + timePart;
    } else if (datePart.length === 10 && value.endsWith(' ')) {
      // User pressed space after complete date
      result = datePart + ' ';
    }
    
    setManualTrade({...manualTrade, [field]: result});
  };
  
  // Edit trade state
  const [showEditTradeModal, setShowEditTradeModal] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [editTradeLoading, setEditTradeLoading] = useState(false);
  const [deleteTradeLoading, setDeleteTradeLoading] = useState(null);
  
  // Delete confirmation modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState(null);
  
  // Strategies state
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyFilter, setSelectedStrategyFilter] = useState('');

  const emotions = ['😊', '😐', '😟', '😤', '🤑', '😰', '💪', '🎯'];
  
  // Helper: Convert check-in data to structured note string for KI Mentor
  const getCheckInNoteString = (data) => {
    const sleepLabel = data.sleepQuality === 'good' ? 'Gut' : data.sleepQuality === 'okay' ? 'Ok' : 'Schlecht';
    return `Stimmung: ${data.mood}/10 | Energie: ${data.energy}/10 | Stress: ${data.stress}/10 | Schlaf: ${sleepLabel}`;
  };
  
  // Helper: Parse note string back to check-in data
  const parseCheckInFromNote = (note) => {
    const defaultData = { mood: 5, energy: 5, stress: 5, sleepQuality: 'okay' };
    if (!note || !note.includes('Stimmung:')) return defaultData;
    
    try {
      const moodMatch = note.match(/Stimmung: (\d+)/);
      const energyMatch = note.match(/Energie: (\d+)/);
      const stressMatch = note.match(/Stress: (\d+)/);
      const sleepMatch = note.match(/Schlaf: (Gut|Ok|Schlecht)/);
      
      return {
        mood: moodMatch ? parseInt(moodMatch[1]) : 5,
        energy: energyMatch ? parseInt(energyMatch[1]) : 5,
        stress: stressMatch ? parseInt(stressMatch[1]) : 5,
        sleepQuality: sleepMatch ? (sleepMatch[1] === 'Gut' ? 'good' : sleepMatch[1] === 'Schlecht' ? 'bad' : 'okay') : 'okay'
      };
    } catch {
      return defaultData;
    }
  };
  
  // Helper: Get mood indicator color based on overall check-in
  const getCheckInIndicator = (data) => {
    const avgScore = (data.mood + data.energy + (10 - data.stress)) / 3;
    if (avgScore >= 7) return { color: 'text-emerald-400', bg: 'bg-emerald-500' };
    if (avgScore >= 5) return { color: 'text-amber-400', bg: 'bg-amber-500' };
    return { color: 'text-rose-400', bg: 'bg-rose-500' };
  };

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    try {
      const response = await fetch(`${API}/strategies`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStrategies(data.strategies || []);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }, []);

  // Fetch exchange rates
  const fetchExchangeRates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/exchange-rates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setExchangeRates(data);
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
    }
  }, []);

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounts`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0].account_id);
          setCurrency(data[0].currency || 'USD');
          setDisplayCurrency(data[0].currency || 'USD');
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  }, [selectedAccount]);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      let url = `${API}/dashboard/stats?account_id=${selectedAccount}&month=${selectedMonth}&year=${selectedYear}`;
      if (selectedStrategyFilter) {
        url += `&strategy_id=${selectedStrategyFilter}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [selectedAccount, selectedMonth, selectedYear, selectedStrategyFilter]);

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      let url = `${API}/dashboard/calendar?account_id=${selectedAccount}&month=${selectedMonth}&year=${selectedYear}`;
      if (selectedStrategyFilter) {
        url += `&strategy_id=${selectedStrategyFilter}`;
      }
      // Add symbol filter if not 'all'
      if (filter && filter !== 'all') {
        url += `&symbol=${encodeURIComponent(filter)}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCalendarData(data);
      }
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  }, [selectedAccount, selectedMonth, selectedYear, selectedStrategyFilter, filter]);

  // Fetch month journals
  const fetchMonthJournals = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const response = await fetch(
        `${API}/journal/month/${selectedAccount}?month=${selectedMonth}&year=${selectedYear}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthJournals(data);
      }
    } catch (error) {
      console.error('Error fetching journals:', error);
    }
  }, [selectedAccount, selectedMonth, selectedYear]);

  // Fetch trades for symbol filter
  const fetchTrades = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      let url = `${API}/trades?account_id=${selectedAccount}&month=${selectedMonth}&year=${selectedYear}`;
      if (selectedStrategyFilter) {
        url += `&strategy_id=${selectedStrategyFilter}`;
      }
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTrades(data);
        // Extract unique symbols
        const symbols = [...new Set(data.map(t => t.symbol).filter(Boolean))].sort();
        setAvailableSymbols(symbols);
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  }, [selectedAccount, selectedMonth, selectedYear, selectedStrategyFilter]);

  useEffect(() => {
    fetchAccounts();
    fetchExchangeRates();
    fetchStrategies();
  }, [fetchAccounts, fetchExchangeRates, fetchStrategies]);

  useEffect(() => {
    fetchStats();
    fetchCalendarData();
    fetchMonthJournals();
    fetchTrades();
  }, [fetchStats, fetchCalendarData, fetchMonthJournals, fetchTrades]);

  useEffect(() => {
    const account = accounts.find(a => a.account_id === selectedAccount);
    if (account) {
      setCurrency(account.currency || 'USD');
    }
  }, [selectedAccount, accounts]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Account limit helpers
  const getAccountLimit = () => {
    if (user?.is_admin === true) return Infinity;
    if (user?.subscription_status !== 'active' && user?.subscription_status !== 'lifetime') return 0;
    const plan = user?.subscription_plan || 'basic';
    if (plan === 'basic') return 1;
    if (plan === 'pro') return 5;
    return Infinity;
  };

  const hasActiveSubscription = () => {
    return user?.is_admin === true || user?.subscription_status === 'active' || user?.subscription_status === 'lifetime';
  };

  const canCreateAccount = () => {
    const limit = getAccountLimit();
    if (limit === Infinity) return true;
    return accounts.length < limit;
  };

  // Handle opening "Add Account" modal or showing upgrade modal
  const handleAddAccountClick = () => {
    setShowAccountDropdown(false);
    if (!hasActiveSubscription()) {
      setShowUpgradeModal(true);
      return;
    }
    if (!canCreateAccount()) {
      setShowUpgradeModal(true);
      return;
    }
    setShowCreateAccountModal(true);
  };

  // Create new account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError('');
    setAccountCreationLoading(true);
    
    try {
      const response = await fetch(`${API}/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newAccountName,
          broker: newAccountBroker || null,
          currency: newAccountCurrency,
        }),
      });
      
      if (response.ok) {
        const newAccount = await response.json();
        setShowCreateAccountModal(false);
        setNewAccountName('');
        setNewAccountBroker('');
        setNewAccountCurrency('USD');
        fetchAccounts();
        // Auto-select the new account
        setTimeout(() => setSelectedAccount(newAccount.account_id), 100);
        toast.success(language === 'de' ? 'Konto erstellt!' : 'Account created!');
      } else {
        if (response.status === 403) {
          setShowCreateAccountModal(false);
          setShowUpgradeModal(true);
          return;
        }
        const err = await response.json().catch(() => ({}));
        if (err.detail?.code === 'ACCOUNT_LIMIT_REACHED' || err.detail?.upgrade_required) {
          setShowCreateAccountModal(false);
          setShowUpgradeModal(true);
          return;
        }
        setAccountError(typeof err.detail === 'string' ? err.detail : 'Fehler beim Erstellen');
      }
    } catch (error) {
      console.error('Account creation error:', error);
      setAccountError('Verbindungsfehler');
    } finally {
      setAccountCreationLoading(false);
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API}/accounts/${accountToDelete.account_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(language === 'de' ? 'Konto gelöscht!' : 'Account deleted!');
        setShowDeleteAccountModal(false);
        setAccountToDelete(null);
        fetchAccounts();
        // Select another account if the deleted one was selected
        if (selectedAccount === accountToDelete.account_id) {
          setSelectedAccount(null);
        }
      } else {
        toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Error deleting account');
      }
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(language === 'de' ? 'Verbindungsfehler' : 'Connection error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Broker name mapping for symbol autocomplete
  // Maps account broker names/ids to JSON broker names (case-insensitive)
  const BROKER_SYMBOL_MAPPING = {
    // Futures Brokers -> CME Group
    'tradovate': 'CME Group',
    'ninjatrader': 'CME Group',
    'apex trader funding': 'CME Group',
    'apex': 'CME Group',
    'topstep': 'CME Group',
    'ftmo': 'CME Group',
    'tradestation': 'CME Group',
    'cme group': 'CME Group',
    // Forex Brokers -> Oanda Forex
    'metatrader 4 (mt4)': 'Oanda Forex',
    'metatrader 5 (mt5)': 'Oanda Forex',
    'metatrader4': 'Oanda Forex',
    'metatrader5': 'Oanda Forex',
    'mt4': 'Oanda Forex',
    'mt5': 'Oanda Forex',
    'oanda': 'Oanda Forex',
    'oanda forex': 'Oanda Forex',
    'pepperstone': 'Oanda Forex',
    'xtb': 'Oanda Forex',
    // Crypto -> appropriate exchange
    'kraken': 'Coinbase',
    'coinbase': 'Coinbase',
    // CFD
    'etoro': 'Interactive Brokers CFD',
    'plus500': 'Interactive Brokers CFD',
    'interactive_brokers': 'Interactive Brokers CFD',
    'interactive brokers': 'Interactive Brokers CFD',
  };

  // Get broker name from current account (with mapping)
  const getCurrentAccountBroker = useMemo(() => {
    const currentAccount = accounts.find(a => a.account_id === selectedAccount);
    const accountBroker = currentAccount?.broker || null;
    
    // Check if direct match exists in JSON
    if (accountBroker && brokerSymbolData.symbols[accountBroker]) {
      return accountBroker;
    }
    
    // Try case-insensitive mapping
    if (accountBroker) {
      const lowerBroker = accountBroker.toLowerCase();
      if (BROKER_SYMBOL_MAPPING[lowerBroker]) {
        return BROKER_SYMBOL_MAPPING[lowerBroker];
      }
    }
    
    return accountBroker;
  }, [accounts, selectedAccount]);

  // Get the original broker name (unmapped) for display
  const getOriginalBrokerName = useMemo(() => {
    const currentAccount = accounts.find(a => a.account_id === selectedAccount);
    return currentAccount?.broker || null;
  }, [accounts, selectedAccount]);

  // Get selected account data including currency
  const selectedAccountData = useMemo(() => {
    return accounts.find(a => a.account_id === selectedAccount) || null;
  }, [accounts, selectedAccount]);

  // Symbol autocomplete handler
  const handleSymbolInputChange = (value) => {
    setManualTrade(prev => ({ ...prev, symbol: value }));
    
    if (!value || value.length < 1) {
      setSymbolSuggestions([]);
      setShowSymbolSuggestions(false);
      return;
    }
    
    // Get broker from current account (with mapping)
    const brokerName = getCurrentAccountBroker;
    
    // Check if broker has symbol data
    if (brokerName && brokerSymbolData.symbols[brokerName]) {
      const brokerSymbols = brokerSymbolData.symbols[brokerName];
      const filtered = brokerSymbols
        .filter(s => s.s.toUpperCase().startsWith(value.toUpperCase()) || 
                     (s.n && s.n.toUpperCase().includes(value.toUpperCase())))
        .slice(0, 15); // Limit to 15 suggestions
      setSymbolSuggestions(filtered);
      setShowSymbolSuggestions(filtered.length > 0);
    } else {
      setSymbolSuggestions([]);
      setShowSymbolSuggestions(false);
    }
  };

  const selectSymbolSuggestion = (symbol) => {
    setManualTrade(prev => ({ ...prev, symbol: symbol.s }));
    setShowSymbolSuggestions(false);
    setSymbolSuggestions([]);
  };

  // CSV/XLSX Import handlers
  const onCsvDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    const isXlsx = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    
    if (isXlsx) {
      // XLSX: Upload as binary to backend for parsing
      const handleXlsx = async () => {
        setImportLoading(true);
        try {
          const formData = new FormData();
          formData.append('file', file);
          const uploadRes = await fetch(`${API}/xlsx/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          if (!uploadRes.ok) {
            const err = await uploadRes.json();
            toast.error(err.detail || 'XLSX Upload fehlgeschlagen');
            return;
          }
          const xlsxData = await uploadRes.json();
          if (xlsxData.sheet_used) {
            toast.success(language === 'de' ? `Tabellenblatt "${xlsxData.sheet_used}" erkannt (${xlsxData.row_count} Zeilen)` : `Sheet "${xlsxData.sheet_used}" detected (${xlsxData.row_count} rows)`);
          }
          setCsvContent(xlsxData.csv_content);
          // Now analyze columns with AI
          const analyzeRes = await fetch(`${API}/csv/analyze-columns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ csv_content: xlsxData.csv_content }),
          });
          if (analyzeRes.ok) {
            const mapping = await analyzeRes.json();
            setCsvMapping(mapping);
            setShowImportModal(false);
            setShowCsvMappingModal(true);
          }
        } catch (error) {
          console.error('XLSX import error:', error);
          toast.error(language === 'de' ? 'XLSX Import fehlgeschlagen' : 'XLSX import failed');
        } finally {
          setImportLoading(false);
        }
      };
      handleXlsx();
    } else {
      // CSV: Read as text
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target.result;
        setCsvContent(content);
        setImportLoading(true);
        try {
          const response = await fetch(`${API}/csv/analyze-columns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ csv_content: content }),
          });
          if (response.ok) {
            const mapping = await response.json();
            setCsvMapping(mapping);
            setShowImportModal(false);
            setShowCsvMappingModal(true);
          }
        } catch (error) {
          console.error('CSV analysis error:', error);
        } finally {
          setImportLoading(false);
        }
      };
      reader.readAsText(file);
    }
  }, [language]);

  const { getRootProps: getCsvRootProps, getInputProps: getCsvInputProps, isDragActive: isCsvDragActive } = useDropzone({
    onDrop: onCsvDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] },
    multiple: false,
  });

  const handleCsvImport = async () => {
    if (!csvContent || !selectedAccount || !csvMapping) return;
    setImportLoading(true);
    try {
      const response = await fetch(`${API}/trades/import-mapped`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: selectedAccount,
          csv_content: csvContent,
          mapping: csvMapping.mapping,
        }),
      });
      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        setShowCsvMappingModal(false);
        setShowSuccessModal(true);
        setCsvContent('');
        setCsvMapping(null);
        fetchStats();
        fetchCalendarData();
      }
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setImportLoading(false);
    }
  };

  // Screenshot Import handlers
  const onScreenshotDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setScreenshotImage(URL.createObjectURL(file));
      setCrop(null);
      setCroppedImageUrl(null);
      setShowCropMode(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setScreenshotBase64(base64);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Validate timestamp format (DD.MM.YYYY HH:MM:SS or DD.MM.YYYY HH:MM)
  const isValidTimestamp = (ts) => {
    if (!ts || typeof ts !== 'string') return false;
    
    // Match DD.MM.YYYY HH:MM:SS or DD.MM.YYYY HH:MM
    const match = ts.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) return false;
    
    const [, day, month, year, hour, minute, second = '0'] = match;
    const d = parseInt(day), m = parseInt(month), y = parseInt(year);
    const h = parseInt(hour), min = parseInt(minute), s = parseInt(second);
    
    // Validate ranges
    if (d < 1 || d > 31) return false;
    if (m < 1 || m > 12) return false;
    if (y < 2000 || y > 2100) return false;
    if (h < 0 || h > 23) return false;
    if (min < 0 || min > 59) return false;
    if (s < 0 || s > 59) return false;
    
    return true;
  };

  // Get timestamp validation error message
  const getTimestampError = (ts) => {
    if (!ts) return null;
    
    const match = ts.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) return language === 'de' ? 'Format: TT.MM.JJJJ HH:MM:SS' : 'Format: DD.MM.YYYY HH:MM:SS';
    
    const [, day, month, , hour, minute, second = '0'] = match;
    const h = parseInt(hour), min = parseInt(minute), s = parseInt(second);
    
    if (h > 23) return language === 'de' ? `Ungültige Stunde: ${h} (max. 23)` : `Invalid hour: ${h} (max. 23)`;
    if (min > 59) return language === 'de' ? `Ungültige Minute: ${min} (max. 59)` : `Invalid minute: ${min} (max. 59)`;
    if (s > 59) return language === 'de' ? `Ungültige Sekunde: ${s} (max. 59)` : `Invalid second: ${s} (max. 59)`;
    if (parseInt(day) > 31) return language === 'de' ? `Ungültiger Tag: ${day}` : `Invalid day: ${day}`;
    if (parseInt(month) > 12) return language === 'de' ? `Ungültiger Monat: ${month}` : `Invalid month: ${month}`;
    
    return null;
  };

  // Validation functions for all field types
  const isValidSymbol = (val) => val && val.trim().length > 0;
  const isValidNumber = (val) => val !== '' && val !== null && val !== undefined && !isNaN(parseFloat(val)) && parseFloat(val) >= 0;
  const isValidPrice = (val) => val !== '' && val !== null && val !== undefined && !isNaN(parseFloat(val)) && parseFloat(val) > 0;
  const isValidQty = (val) => val !== '' && val !== null && val !== undefined && !isNaN(parseFloat(val)) && parseFloat(val) > 0;

  // Get field validation error
  const getFieldError = (fieldType, val) => {
    if (!val && val !== 0) return null; // Don't show error for empty fields initially
    
    switch(fieldType) {
      case 'symbol':
        return !isValidSymbol(val) ? (language === 'de' ? 'Symbol erforderlich' : 'Symbol required') : null;
      case 'price':
        if (val === '' || val === null) return null;
        return !isValidPrice(val) ? (language === 'de' ? 'Ungültiger Preis' : 'Invalid price') : null;
      case 'qty':
        if (val === '' || val === null) return null;
        return !isValidQty(val) ? (language === 'de' ? 'Ungültige Menge' : 'Invalid quantity') : null;
      default:
        return null;
    }
  };

  // Check if field has error (for red border)
  const hasFieldError = (fieldType, val) => {
    if (!val && val !== 0) return false;
    return getFieldError(fieldType, val) !== null;
  };

  // Crop image function
  const getCroppedImg = async (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            url: URL.createObjectURL(blob),
            base64: reader.result.split(',')[1]
          });
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleCropComplete = async () => {
    if (!crop || !crop.width || !crop.height) {
      toast.error(language === 'de' ? 'Bitte einen Bereich auswählen' : 'Please select an area');
      return;
    }
    const imgElement = document.querySelector('.crop-image-element');
    if (!imgElement) return;
    
    const result = await getCroppedImg(imgElement, crop);
    if (result) {
      setCroppedImageUrl(result.url);
      setScreenshotBase64(result.base64);
      setShowCropMode(false);
      toast.success(language === 'de' ? 'Bild zugeschnitten!' : 'Image cropped!');
    }
  };

  const { getRootProps: getScreenshotRootProps, getInputProps: getScreenshotInputProps, isDragActive: isScreenshotDragActive } = useDropzone({
    onDrop: onScreenshotDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    multiple: false,
  });

  const handleScreenshotAnalyze = async () => {
    if (!screenshotBase64 || !selectedAccount) {
      toast.error(language === 'de' ? 'Kein Screenshot ausgewählt' : 'No screenshot selected');
      return;
    }
    setScreenshotLoading(true);
    try {
      const response = await fetch(`${API}/analyze-screenshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          image_base64: screenshotBase64,
          account_id: selectedAccount,
        }),
      });
      if (response.ok) {
        const analysis = await response.json();
        setScreenshotAnalysis(analysis);
        // Pre-fill manual trade with analysis results
        setManualTrade({
          symbol: analysis.symbol || '',
          buy_price: analysis.buy_price?.toString() || '',
          sell_price: analysis.sell_price?.toString() || '',
          qty: analysis.qty?.toString() || '',
          bought_timestamp: analysis.bought_timestamp || '',
          sold_timestamp: analysis.sold_timestamp || '',
          notes: '',
        });
        
        toast.success(language === 'de' ? 'Screenshot analysiert!' : 'Screenshot analyzed!');
        
        if (analysis.missing_fields?.length > 0) {
          setShowScreenshotModal(false);
          setShowManualEntryModal(true);
        } else {
          // Verify all required fields are actually present (not null/undefined)
          const requiredFields = ['symbol', 'buy_price', 'sell_price', 'qty', 'bought_timestamp', 'sold_timestamp'];
          const actuallyMissing = requiredFields.filter(f => !analysis[f] && analysis[f] !== 0);
          
          if (actuallyMissing.length > 0) {
            // KI said no missing fields, but some are null - open manual entry
            console.warn('KI reported no missing fields, but these are null:', actuallyMissing);
            setShowScreenshotModal(false);
            setShowManualEntryModal(true);
            toast.warning(
              language === 'de' ? 'Bitte fehlende Felder ergänzen' : 'Please fill missing fields',
              { description: actuallyMissing.join(', ') }
            );
          } else {
            // All fields found, create trade directly
            await handleCreateTradeFromScreenshot(analysis);
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(
          language === 'de' ? 'Analyse fehlgeschlagen' : 'Analysis failed',
          { description: errorData.detail || 'Server error' }
        );
      }
    } catch (error) {
      console.error('Screenshot analysis error:', error);
      toast.error(
        language === 'de' ? 'Verbindungsfehler' : 'Connection error',
        { description: language === 'de' ? 'Bitte versuchen Sie es erneut' : 'Please try again' }
      );
    } finally {
      setScreenshotLoading(false);
    }
  };

  // Upload screenshot to GridFS and get permanent URL
  const uploadScreenshotToStorage = async (base64Data) => {
    try {
      const response = await fetch(`${API}/screenshots/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          image_base64: base64Data,
          filename: `trade_screenshot_${Date.now()}.png`
        }),
      });
      
      if (response.status === 401) {
        toast.error(
          language === 'de' ? 'Sitzung abgelaufen' : 'Session expired',
          { description: language === 'de' ? 'Bitte neu einloggen' : 'Please log in again' }
        );
        logout();
        return null;
      }
      
      if (response.ok) {
        const result = await response.json();
        return result.screenshot_url; // Returns /api/screenshots/ss_xxxxx
      }
      return null;
    } catch (error) {
      console.error('Screenshot upload error:', error);
      return null;
    }
  };

  const handleCreateTradeFromScreenshot = async (data = null) => {
    const tradeData = data || manualTrade;
    setImportLoading(true);
    try {
      // First, try to upload the screenshot to GridFS for a permanent URL
      let screenshotData = null;
      if (screenshotBase64) {
        const permanentUrl = await uploadScreenshotToStorage(screenshotBase64);
        if (permanentUrl) {
          screenshotData = { screenshot_url: permanentUrl };
        } else {
          // Fallback: Store Base64 directly (for smaller images)
          // Limit to ~500KB to avoid DB bloat
          if (screenshotBase64.length < 700000) {
            screenshotData = { screenshot_base64: `data:image/png;base64,${screenshotBase64}` };
            console.log('Using Base64 fallback for screenshot storage');
          } else {
            console.warn('Screenshot too large for Base64 fallback');
          }
        }
      }
      
      const response = await fetch(`${API}/trades/from-screenshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: selectedAccount,
          ...tradeData,
          buy_price: parseFloat(tradeData.buy_price),
          sell_price: parseFloat(tradeData.sell_price),
          qty: parseFloat(tradeData.qty),
          direction: tradeData.direction || 'long',
          screenshot_url: screenshotData?.screenshot_url || null,
          screenshot_base64: screenshotData?.screenshot_base64 || null,
          strategy_id: tradeData.strategy_id || null,
        }),
      });
      
      if (response.status === 401) {
        toast.error(
          language === 'de' ? 'Sitzung abgelaufen' : 'Session expired',
          { description: language === 'de' ? 'Bitte neu einloggen' : 'Please log in again' }
        );
        logout();
        return;
      }
      
      if (response.ok) {
        const result = await response.json();
        setImportResult({ imported: 1, trade_id: result.trade_id });
        setShowManualEntryModal(false);
        setShowScreenshotModal(false);
        setShowSuccessModal(true);
        setScreenshotImage(null);
        setScreenshotBase64('');
        setScreenshotAnalysis(null);
        setManualTrade({ symbol: '', buy_price: '', sell_price: '', qty: '', bought_timestamp: '', sold_timestamp: '', notes: '', strategy_id: '', direction: 'long' });
        fetchStats();
        fetchCalendarData();
        fetchTrades();
        toast.success(language === 'de' ? 'Trade erfolgreich erstellt!' : 'Trade created successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(
          language === 'de' ? 'Fehler beim Erstellen' : 'Failed to create trade',
          { description: errorData.detail || 'Server error' }
        );
      }
    } catch (error) {
      console.error('Trade creation error:', error);
      toast.error(language === 'de' ? 'Verbindungsfehler' : 'Connection error');
    } finally {
      setImportLoading(false);
    }
  };

  // Day Journal handlers
  const openDayModal = async (date) => {
    setSelectedDate(date);
    // Fetch day journal
    try {
      const response = await fetch(`${API}/journal/day/${selectedAccount}/${date}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDayJournal(data);
        setEmotionEmoji(data.journal?.emoji || '');
        setEmotionNote(data.journal?.emotion_note || '');
        setDayNotes(data.journal?.day_notes || '');
        setDayScreenshots(data.journal?.screenshots || []);
        // Parse check-in data from emotion_note
        const parsedCheckIn = parseCheckInFromNote(data.journal?.emotion_note || '');
        setCheckInData(parsedCheckIn);
      }
    } catch (error) {
      console.error('Error fetching day journal:', error);
    }
    setShowDayModal(true);
  };

  const handleSaveDayJournal = async () => {
    if (!selectedDate || !selectedAccount) return;
    
    // Convert check-in data to structured note for KI Mentor
    const generatedNote = getCheckInNoteString(checkInData);
    
    try {
      await fetch(`${API}/journal/day`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: selectedDate,
          account_id: selectedAccount,
          emoji: '', // No longer using emoji
          emotion_note: generatedNote,
          day_notes: dayNotes,
          screenshots: dayScreenshots,
        }),
      });
      setShowDayModal(false);
      fetchMonthJournals();
      fetchCalendarData();
      toast.success(language === 'de' ? 'Check-in gespeichert' : 'Check-in saved');
    } catch (error) {
      console.error('Error saving journal:', error);
      toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Error saving');
    }
  };

  const handleDayScreenshotUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      // Add to screenshots array
      setDayScreenshots([...dayScreenshots, base64]);
    };
    reader.readAsDataURL(file);
  };

  const removeDayScreenshot = (index) => {
    setDayScreenshots(dayScreenshots.filter((_, i) => i !== index));
  };

  // Update trade strategy from day modal
  const handleUpdateTradeStrategy = async (tradeId, strategyId) => {
    try {
      const response = await fetch(`${API}/trades/${tradeId}/strategy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ strategy_id: strategyId || null }),
      });
      if (response.ok) {
        // Update local state
        setDayJournal(prev => ({
          ...prev,
          trades: prev.trades.map(trade => 
            trade.trade_id === tradeId 
              ? { ...trade, strategy_id: strategyId || null }
              : trade
          )
        }));
        toast.success(language === 'de' ? 'Strategie aktualisiert' : 'Strategy updated');
      }
    } catch (error) {
      console.error('Error updating trade strategy:', error);
      toast.error(language === 'de' ? 'Fehler beim Aktualisieren' : 'Error updating');
    }
  };

  // Open edit trade modal
  const handleEditTrade = (trade) => {
    // Infer direction from trade data if not set
    // For a long trade: we buy first, then sell (profit if sell > buy)
    // For a short trade: we sell first, then buy (profit if buy > sell)
    // So if P&L > 0 and sell > buy -> long, if P&L > 0 and buy > sell -> short
    let inferredDirection = trade.direction;
    if (!inferredDirection) {
      const pnl = trade.pnl || 0;
      const buyPrice = parseFloat(trade.buy_price) || 0;
      const sellPrice = parseFloat(trade.sell_price) || 0;
      
      if (pnl >= 0) {
        // Profit trade: direction matches price movement
        inferredDirection = sellPrice >= buyPrice ? 'long' : 'short';
      } else {
        // Loss trade: direction is opposite of price movement
        inferredDirection = sellPrice < buyPrice ? 'long' : 'short';
      }
    }
    
    // Format timestamps for datetime-local input (YYYY-MM-DDTHH:MM)
    const formatTimestamp = (ts) => {
      if (!ts) return '';
      try {
        const date = new Date(ts);
        if (isNaN(date.getTime())) return '';
        // Format as local time
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (e) {
        return '';
      }
    };
    
    setEditingTrade({
      ...trade,
      buy_price: trade.buy_price || '',
      sell_price: trade.sell_price || '',
      qty: trade.qty || '',
      notes: trade.notes || '',
      strategy_id: trade.strategy_id || '',
      direction: inferredDirection || 'long',
      bought_timestamp: formatTimestamp(trade.bought_timestamp),
      sold_timestamp: formatTimestamp(trade.sold_timestamp),
    });
    setShowEditTradeModal(true);
  };

  // Save edited trade
  const handleSaveEditedTrade = async () => {
    if (!editingTrade) return;
    setEditTradeLoading(true);
    
    try {
      const updateData = {
        symbol: editingTrade.symbol,
        buy_price: parseFloat(editingTrade.buy_price) || 0,
        sell_price: parseFloat(editingTrade.sell_price) || 0,
        qty: parseFloat(editingTrade.qty) || 0,
        notes: editingTrade.notes || '',
        strategy_id: editingTrade.strategy_id || null,
        direction: editingTrade.direction || 'long',
        bought_timestamp: editingTrade.bought_timestamp ? new Date(editingTrade.bought_timestamp).toISOString() : null,
        sold_timestamp: editingTrade.sold_timestamp ? new Date(editingTrade.sold_timestamp).toISOString() : null,
      };
      
      const response = await fetch(`${API}/trades/${editingTrade.trade_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData),
      });
      
      if (response.ok) {
        const updatedTrade = await response.json();
        // Update local state
        setDayJournal(prev => ({
          ...prev,
          trades: prev.trades.map(trade => 
            trade.trade_id === editingTrade.trade_id ? updatedTrade : trade
          )
        }));
        setShowEditTradeModal(false);
        setEditingTrade(null);
        toast.success(language === 'de' ? 'Trade aktualisiert' : 'Trade updated');
        // Refresh data
        fetchStats();
        fetchCalendarData();
        fetchTrades();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving trade:', error);
      toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Error saving');
    } finally {
      setEditTradeLoading(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteTrade = (trade) => {
    setTradeToDelete(trade);
    setShowDeleteConfirmModal(true);
  };

  // Confirm delete trade
  const confirmDeleteTrade = async () => {
    if (!tradeToDelete) return;
    
    setDeleteTradeLoading(tradeToDelete.trade_id);
    
    try {
      const response = await fetch(`${API}/trades/${tradeToDelete.trade_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        // Update local state
        setDayJournal(prev => ({
          ...prev,
          trades: prev.trades.filter(trade => trade.trade_id !== tradeToDelete.trade_id)
        }));
        toast.success(language === 'de' ? 'Trade gelöscht' : 'Trade deleted');
        // Refresh data
        fetchStats();
        fetchCalendarData();
        fetchTrades();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting trade:', error);
      toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Error deleting');
    } finally {
      setDeleteTradeLoading(null);
      setShowDeleteConfirmModal(false);
      setTradeToDelete(null);
    }
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const convertCurrency = (value, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency || !exchangeRates) return value;
    if (fromCurrency === 'USD' && toCurrency === 'EUR') {
      return value * (exchangeRates.USD_EUR || 0.926);
    } else if (fromCurrency === 'EUR' && toCurrency === 'USD') {
      return value * (exchangeRates.EUR_USD || 1.08);
    }
    return value;
  };

  const formatCurrency = (value) => {
    const convertedValue = convertCurrency(value, currency, displayCurrency);
    const symbol = displayCurrency === 'EUR' ? '€' : '$';
    const formatted = Math.abs(convertedValue).toFixed(2);
    if (convertedValue >= 0) return `+${formatted}${symbol}`;
    return `-${formatted}${symbol}`;
  };

  const formatPercent = (value) => `${value.toFixed(2)}%`;

  // Format date for display (DD.MM.YYYY for German)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    // If it's YYYY-MM-DD format, convert to DD.MM.YYYY
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}.${month}.${year}`;
    }
    return dateStr;
  };

  // Resolve screenshot URL (handle both relative /api/... and absolute URLs)
  const getScreenshotUrl = (trade) => {
    // First check for Base64 fallback
    if (trade.screenshot_base64) {
      return trade.screenshot_base64;
    }
    
    const url = trade.screenshot_url;
    if (!url) return null;
    
    // Blob URLs are no longer valid after page refresh - they were temporary
    if (url.startsWith('blob:')) {
      return null;
    }
    // If it's a relative API path, prepend the API base URL
    if (url.startsWith('/api/')) {
      const baseUrl = API.replace('/api', '');
      return `${baseUrl}${url}`;
    }
    // If it's already a full URL, return as-is
    return url;
  };

  // Check if screenshot is available (either URL or Base64)
  const hasValidScreenshot = (trade) => {
    if (trade.screenshot_base64) return true;
    const url = trade.screenshot_url;
    if (!url) return false;
    if (url.startsWith('blob:')) return false;
    return true;
  };

  const generateCalendarDays = () => {
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ empty: true });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData.find(d => d.date === dateStr);
      const journal = monthJournals[dateStr];
      days.push({
        day,
        date: dateStr,
        pnl: dayData?.pnl || 0,
        tradeCount: dayData?.trade_count || 0,
        hasTrades: !!dayData,
        hasJournal: !!journal,
        hasCheckIn: !!(journal?.emotion_note && journal.emotion_note.includes('Stimmung:')),
        hasScreenshots: journal?.screenshots?.length > 0,
      });
    }
    return days;
  };

  // Generate calendar weeks with weekly stats
  const generateCalendarWeeks = () => {
    const days = generateCalendarDays();
    const weeks = [];
    
    for (let i = 0; i < days.length; i += 7) {
      const weekDays = days.slice(i, i + 7);
      
      // Pad incomplete weeks
      while (weekDays.length < 7) {
        weekDays.push({ empty: true });
      }
      
      // Calculate weekly stats
      const weekPnl = weekDays.reduce((sum, d) => sum + (d.pnl || 0), 0);
      const weekTrades = weekDays.reduce((sum, d) => sum + (d.tradeCount || 0), 0);
      const tradingDays = weekDays.filter(d => d.hasTrades).length;
      
      weeks.push({
        days: weekDays,
        stats: {
          pnl: weekPnl,
          trades: weekTrades,
          tradingDays: tradingDays,
          hasActivity: weekTrades > 0
        }
      });
    }
    
    return weeks;
  };

  // Calculate filtered stats based on selected symbol
  const filteredStats = (() => {
    if (filter === 'all' || !trades.length) {
      return stats;
    }
    
    const filteredTrades = trades.filter(t => t.symbol === filter);
    if (!filteredTrades.length) {
      return {
        total_trades: 0,
        winners: 0,
        losers: 0,
        total_pnl: 0,
        avg_pnl_per_trade: 0,
        best_trade: 0,
        worst_trade: 0,
        win_rate: 0,
        profit_factor: 0,
        max_drawdown: 0,
        avg_win: 0,
        twr_percent: 0
      };
    }
    
    const pnls = filteredTrades.map(t => t.pnl);
    const winners = pnls.filter(p => p > 0);
    const losers = pnls.filter(p => p < 0);
    
    const totalPnl = pnls.reduce((sum, p) => sum + p, 0);
    const totalTrades = pnls.length;
    const winCount = winners.length;
    
    const winRate = totalTrades > 0 ? (winCount / totalTrades * 100) : 0;
    const avgPnl = totalTrades > 0 ? totalPnl / totalTrades : 0;
    const avgWin = winners.length > 0 ? winners.reduce((s, p) => s + p, 0) / winners.length : 0;
    
    const grossProfit = winners.reduce((s, p) => s + p, 0);
    const grossLoss = Math.abs(losers.reduce((s, p) => s + p, 0));
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? grossProfit : 0;
    
    // Max drawdown calculation
    // Use reference capital based on total gains or assumed starting capital
    // This gives a more realistic drawdown percentage
    let cumulative = 0;
    let peak = 0;
    let maxDrawdownAmount = 0;
    
    // Calculate total gains and losses for reference
    const totalGains = pnls.filter(p => p > 0).reduce((s, p) => s + p, 0);
    const totalLosses = Math.abs(pnls.filter(p => p < 0).reduce((s, p) => s + p, 0));
    
    // Reference capital: use the larger of total gains, total losses, or 10000 as assumed starting capital
    const referenceCapital = Math.max(totalGains, 10000, totalLosses);
    
    for (const pnl of pnls) {
      cumulative += pnl;
      if (cumulative > peak) {
        peak = cumulative;
      }
      // Calculate drawdown amount from peak
      if (cumulative < peak) {
        const drawdownAmount = peak - cumulative;
        if (drawdownAmount > maxDrawdownAmount) {
          maxDrawdownAmount = drawdownAmount;
        }
      }
    }
    
    // Calculate drawdown percentage relative to reference capital
    let maxDd = 0;
    if (referenceCapital > 0) {
      maxDd = (maxDrawdownAmount / referenceCapital) * 100;
    }
    
    // Cap at 100% for sanity
    maxDd = Math.min(maxDd, 100);
    
    return {
      total_trades: totalTrades,
      winners: winCount,
      losers: losers.length,
      total_pnl: Math.round(totalPnl * 100) / 100,
      avg_pnl_per_trade: Math.round(avgPnl * 100) / 100,
      best_trade: winners.length ? Math.round(Math.max(...winners) * 100) / 100 : 0,
      worst_trade: losers.length ? Math.round(Math.min(...losers) * 100) / 100 : 0,
      win_rate: Math.round(winRate * 100) / 100,
      profit_factor: Math.round(profitFactor * 100) / 100,
      max_drawdown: Math.round(maxDd * 100) / 100,
      avg_win: Math.round(avgWin * 100) / 100,
      twr_percent: Math.round(totalPnl / 10000 * 100 * 100) / 100
    };
  })();

  const radarData = filteredStats ? [
    { metric: 'Win', value: Math.min(filteredStats.win_rate, 100), fullMark: 100 },
    { metric: 'PF', value: Math.min(filteredStats.profit_factor * 20, 100), fullMark: 100 },
    { metric: 'R:R', value: 50, fullMark: 100 },
    { metric: 'DD', value: Math.max(100 - filteredStats.max_drawdown * 10, 0), fullMark: 100 },
  ] : [];

  const weekDays = [t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday'), t('sunday')];
  const winRatePercent = filteredStats?.win_rate || 0;
  const circumference = 2 * Math.PI * 70;
  const strokeDashoffset = circumference - (winRatePercent / 100) * circumference;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <Sidebar user={user} onLogout={handleLogout} />

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Header with Account Selector */}
          <header className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Account Selector Dropdown with permanent + Button */}
              <div className="flex items-center gap-1">
                <div className="relative">
                  <button 
                    onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-l-lg hover:bg-zinc-800 transition-colors min-w-[200px]"
                    data-testid="account-dropdown-btn"
                  >
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-left truncate">
                      {accounts.length === 0 
                        ? (language === 'de' ? 'Kein Konto' : 'No account')
                        : accounts.find(a => a.account_id === selectedAccount)?.name || (language === 'de' ? 'Konto wählen' : 'Select account')
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showAccountDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showAccountDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl z-50 py-1 max-h-[300px] overflow-y-auto">
                      {accounts.map((acc) => (
                        <div
                          key={acc.account_id}
                          className={`w-full px-3 py-2 hover:bg-zinc-800 transition-colors flex items-center gap-2 ${
                            selectedAccount === acc.account_id ? 'bg-zinc-800 text-primary' : ''
                          }`}
                        >
                          <button
                            onClick={() => {
                              setSelectedAccount(acc.account_id);
                              setShowAccountDropdown(false);
                            }}
                            className="flex-1 flex items-center gap-2 text-left"
                            data-testid={`account-option-${acc.account_id}`}
                          >
                            <Briefcase className="h-4 w-4 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="truncate font-medium">{acc.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{acc.broker || 'N/A'}</p>
                            </div>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAccountToDelete(acc);
                              setShowDeleteAccountModal(true);
                              setShowAccountDropdown(false);
                            }}
                            className="p-1.5 rounded hover:bg-rose-500/20 text-muted-foreground hover:text-rose-400 transition-colors"
                            data-testid={`delete-account-${acc.account_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Permanent + Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleAddAccountClick}
                      className="flex items-center justify-center w-10 h-10 bg-zinc-900 border border-zinc-800 border-l-0 rounded-r-lg hover:bg-primary hover:text-primary-foreground transition-colors"
                      data-testid="add-account-btn"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{language === 'de' ? 'Konto hinzufügen' : 'Add Account'}</TooltipContent>
                </Tooltip>
              </div>
              
              <div>
                <h1 className="text-2xl font-heading font-bold">{t('tradingJournal')}</h1>
                <p className="text-sm text-muted-foreground">{t('evaluationFor')} {getMonth(selectedMonth - 1)} {selectedYear}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                <SelectTrigger className="w-24" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">$ USD</SelectItem>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Strategy Filter */}
              {strategies.length > 0 && (
                <Select value={selectedStrategyFilter || 'all'} onValueChange={(val) => setSelectedStrategyFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="w-44" data-testid="strategy-filter-select">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder={language === 'de' ? 'Alle Strategien' : 'All Strategies'} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="text-muted-foreground">{language === 'de' ? 'Alle Strategien' : 'All Strategies'}</span>
                    </SelectItem>
                    {strategies.map((s) => (
                      <SelectItem key={s.strategy_id} value={s.strategy_id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1">
                <button onClick={() => navigateMonth('prev')} className="p-2 hover:bg-zinc-800 rounded-md transition-colors" data-testid="prev-month-btn">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-4 font-medium min-w-[140px] text-center">{getMonth(selectedMonth - 1)} {selectedYear}</span>
                <button onClick={() => navigateMonth('next')} className="p-2 hover:bg-zinc-800 rounded-md transition-colors" data-testid="next-month-btn">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Trade hinzufügen Button */}
              <Button 
                onClick={() => setShowAddTradeModal(true)} 
                className="glow-primary" 
                data-testid="add-trade-btn"
                disabled={!selectedAccount}
              >
                <Plus className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Trade hinzufügen' : 'Add Trade'}
              </Button>
              
              {/* PDF Export Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline"
                    onClick={() => setShowExportModal(true)} 
                    data-testid="export-pdf-btn"
                    disabled={accounts.length === 0}
                  >
                    <FileDown className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{language === 'de' ? 'PDF Export' : 'PDF Export'}</TooltipContent>
              </Tooltip>
            </div>
          </header>

          {/* No Account Message */}
          {accounts.length === 0 && (
            <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
              <CardContent className="py-8 text-center">
                <Briefcase className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  {language === 'de' ? 'Erstelle zuerst ein Trading-Konto, um loszulegen.' : 'Create a trading account first to get started.'}
                </p>
                <Button onClick={handleAddAccountClick} data-testid="create-account-link">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('createAccount')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Symbol Filter */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <button 
              onClick={() => setFilter('all')} 
              className={`filter-btn ${filter === 'all' ? 'filter-btn-active' : 'filter-btn-inactive'}`} 
              data-testid="filter-all-btn"
            >
              {t('all')}
            </button>
            {availableSymbols.map((symbol) => (
              <button 
                key={symbol} 
                onClick={() => setFilter(symbol)} 
                className={`filter-btn ${filter === symbol ? 'filter-btn-active' : 'filter-btn-inactive'}`}
                data-testid={`filter-${symbol}-btn`}
              >
                {symbol}
              </button>
            ))}
            {availableSymbols.length === 0 && (
              <span className="text-sm text-muted-foreground italic">
                {language === 'de' ? 'Keine Trades vorhanden' : 'No trades available'}
              </span>
            )}
          </div>

          {/* Trading Day Widget with Economic Calendar */}
          <TradingDayWidget />

          {/* Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Main Stats Cards */}
            <div className="lg:col-span-2 grid grid-cols-3 gap-4">
              <Card className="stat-card">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">{t('twrPercent')}</p>
                  <p className={`text-3xl font-mono font-normal ${(filteredStats?.twr_percent || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {formatPercent(filteredStats?.twr_percent || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">{t('winRate')}</p>
                  <p className="text-3xl font-mono font-normal text-emerald-400">{formatPercent(filteredStats?.win_rate || 0)}</p>
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-1">{t('profitFactor')}</p>
                  <p className="text-3xl font-mono font-normal text-emerald-400">{(filteredStats?.profit_factor || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Stats */}
            <Card className="stat-card row-span-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {t('detailedEvaluation')}
                  <Tooltip><TooltipTrigger><Info className="h-4 w-4 text-muted-foreground" /></TooltipTrigger><TooltipContent>Detailed trading metrics</TooltipContent></Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">{t('totalTrades')}</span><span className="font-mono">{filteredStats?.total_trades || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('winnersLosers')}</span><span className="font-mono">{filteredStats?.winners || 0} / {filteredStats?.losers || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('totalPnl')}</span><span className={`font-mono ${(filteredStats?.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency(filteredStats?.total_pnl || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('avgPnlPerTrade')}</span><span className={`font-mono ${(filteredStats?.avg_pnl_per_trade || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency(filteredStats?.avg_pnl_per_trade || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('bestTrade')}</span><span className="font-mono text-emerald-400">{formatCurrency(filteredStats?.best_trade || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('worstTrade')}</span><span className="font-mono text-rose-400">{formatCurrency(filteredStats?.worst_trade || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('avgWin')}</span><span className="font-mono text-emerald-400">{formatPercent(filteredStats?.avg_win ? (filteredStats.avg_win / (filteredStats.total_pnl || 1) * 100) : 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('profitFactor')}</span><span className="font-mono text-emerald-400">{(filteredStats?.profit_factor || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t('maxDrawdown')}</span><span className="font-mono text-rose-400">{formatPercent(filteredStats?.max_drawdown || 0)}</span></div>
              </CardContent>
            </Card>

            {/* Win Rate Gauge & Performance */}
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              <Card className="stat-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">{t('winRate')}<Tooltip><TooltipTrigger><Info className="h-4 w-4 text-muted-foreground" /></TooltipTrigger><TooltipContent>Percentage of winning trades</TooltipContent></Tooltip></CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center py-4">
                  <div className="relative w-44 h-44">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                      <circle cx="80" cy="80" r="70" fill="none" stroke="hsl(var(--border))" strokeWidth="10" />
                      <circle cx="80" cy="80" r="70" fill="none" stroke="hsl(var(--primary))" strokeWidth="10" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="transition-all duration-700 ease-out" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">Win Rate</span>
                      <span className="text-2xl font-mono font-normal text-white mt-1">{filteredStats?.winners || 0}/{filteredStats?.total_trades || 0}</span>
                      <span className="text-sm text-muted-foreground">Trades</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">{t('performance')}<Tooltip><TooltipTrigger><Info className="h-4 w-4 text-muted-foreground" /></TooltipTrigger><TooltipContent>Performance metrics overview</TooltipContent></Tooltip></CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={160}>
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                      <Radar dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Trading Calendar */}
            <div className="lg:col-span-2">
              <Card className="stat-card overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {t('tradingCalendar')}
                    <Tooltip><TooltipTrigger><Info className="h-4 w-4 text-muted-foreground" /></TooltipTrigger><TooltipContent>{language === 'de' ? 'Klicke auf einen Tag mit Trades für Details, oder auf einen leeren Tag zum Hinzufügen' : 'Click a day with trades for details, or an empty day to add one'}</TooltipContent></Tooltip>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Header row: Week days + Weekly column */}
                  <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: 'repeat(7, 1fr) 100px' }}>
                    {weekDays.map((day) => (
                      <div key={day} className="text-center text-xs font-medium text-zinc-400 py-2 tracking-wider uppercase">
                        {day.slice(0, 2)}
                      </div>
                    ))}
                    <div className="text-center text-xs font-medium text-emerald-400 py-2 tracking-wider uppercase">
                      {language === 'de' ? 'Woche' : 'Week'}
                    </div>
                  </div>
                  
                  {/* Calendar rows with weekly stats */}
                  {generateCalendarWeeks().map((week, weekIndex) => (
                    <div 
                      key={weekIndex} 
                      className="grid gap-2 mb-2" 
                      style={{ gridTemplateColumns: 'repeat(7, 1fr) 100px' }}
                    >
                      {/* Day cells */}
                      {week.days.map((day, dayIndex) => {
                        const isProfit = day.pnl > 0;
                        const isLoss = day.pnl < 0;
                        const hasActivity = day.hasTrades;
                        
                        // P2: Check if this day is today
                        const today = new Date();
                        const isToday = !day.empty && 
                          day.day === today.getDate() && 
                          selectedMonth === today.getMonth() + 1 && 
                          selectedYear === today.getFullYear();
                        
                        // Convert P&L to display currency
                        const convertedPnl = convertCurrency(day.pnl, currency, displayCurrency);
                        const currencySymbol = displayCurrency === 'USD' ? '$' : 
                                              displayCurrency === 'EUR' ? '€' : 
                                              displayCurrency === 'GBP' ? '£' : 
                                              displayCurrency === 'CHF' ? 'CHF ' : '';
                        
                        // Determine glow class - einheitlicher Style, nur Farbe unterschiedlich
                        let glowClass = '';
                        let borderClass = 'border border-white/[0.06]';
                        
                        if (!day.empty) {
                          if (isToday && !hasActivity) {
                            // Heute ohne Trades - neutral weiß
                            glowClass = 'today-glow';
                            borderClass = 'border border-white/30';
                          } else if (hasActivity) {
                            // Tag mit Trades - profit/loss glow
                            glowClass = isProfit ? 'profit-glow' : 'loss-glow';
                            borderClass = isProfit ? 'border border-emerald-500/30' : 'border border-rose-500/30';
                          }
                        }
                        
                        return (
                          <div 
                            key={dayIndex} 
                            className={`
                              min-h-[80px] cursor-pointer rounded-xl
                              transition-all duration-200 ease-out relative
                              flex flex-col items-center justify-center
                              ${day.empty ? 'bg-transparent' : `${borderClass} ${glowClass}`}
                              ${!day.empty && !glowClass ? 'bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12]' : ''}
                              ${!day.empty ? 'hover:scale-[1.02]' : ''}
                            `}
                            onClick={() => {
                              if (day.empty) return;
                              if (day.hasTrades) {
                                openDayModal(day.date);
                              } else {
                                setPreselectedTradeDate(day.date);
                                setShowAddTradeModal(true);
                              }
                            }}
                            data-testid={day.empty ? undefined : `calendar-day-${day.day}`}
                          >
                            {!day.empty && (
                              <>
                                {/* Day number - top right */}
                                <span className={`absolute top-2 right-2 text-sm font-medium ${isToday ? 'text-emerald-400' : hasActivity ? 'text-zinc-300' : 'text-zinc-500'}`}>
                                  {day.day}
                                </span>
                                
                                {/* P&L - centered and prominent */}
                                {hasActivity && (
                                  <span className={`
                                    text-base font-mono
                                    ${isProfit ? 'text-emerald-400' : 'text-rose-400'}
                                  `}>
                                    {convertedPnl >= 0 ? '' : '-'}{currencySymbol}{Math.abs(convertedPnl).toLocaleString('de-DE', { minimumFractionDigits: convertedPnl % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 })}
                                  </span>
                                )}
                                
                                {/* Check-in & Screenshot indicators - bottom */}
                                {(day.hasCheckIn || day.hasScreenshots) && (
                                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
                                    {day.hasCheckIn && (
                                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Check-in vorhanden" />
                                    )}
                                    {day.hasScreenshots && (
                                      <Camera className="h-3 w-3 text-emerald-400" />
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Weekly stats column */}
                      {(() => {
                        // Convert weekly P&L to display currency
                        const convertedWeekPnl = convertCurrency(week.stats.pnl, currency, displayCurrency);
                        const currencySymbol = displayCurrency === 'USD' ? '$' : 
                                              displayCurrency === 'EUR' ? '€' : 
                                              displayCurrency === 'GBP' ? '£' : 
                                              displayCurrency === 'CHF' ? 'CHF ' : '';
                        
                        // Einheitlicher glow - gleicher Style wie Tage
                        const weekGlowClass = week.stats.hasActivity 
                          ? week.stats.pnl >= 0 ? 'weekly-profit-glow' : 'weekly-loss-glow'
                          : '';
                        const weekBorderClass = week.stats.hasActivity
                          ? week.stats.pnl >= 0 ? 'border border-emerald-500/30' : 'border border-rose-500/30'
                          : 'border border-white/[0.06]';
                        
                        return (
                          <div className={`
                            min-h-[80px] rounded-xl p-2 flex flex-col justify-center items-center
                            ${weekBorderClass} ${weekGlowClass}
                            ${!week.stats.hasActivity ? 'bg-white/[0.02]' : ''}
                          `}>
                            {week.stats.hasActivity ? (
                              <>
                                <span className={`
                                  text-sm font-mono font-normal
                                  ${week.stats.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}
                                `}>
                                  {convertedWeekPnl >= 0 ? '' : '-'}{currencySymbol}{Math.abs(convertedWeekPnl).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                                <span className="text-[10px] text-zinc-500 mt-1">
                                  {week.stats.trades} {week.stats.trades === 1 ? 'Trade' : 'Trades'}
                                </span>
                                <span className="text-[10px] text-zinc-500">
                                  {week.stats.tradingDays} {week.stats.tradingDays === 1 ? (language === 'de' ? 'Tag' : 'Day') : (language === 'de' ? 'Tage' : 'Days')}
                                </span>
                              </>
                            ) : (
                              <span className="text-[10px] text-zinc-600">—</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                  
                  {/* Calendar legend */}
                  <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-zinc-800/50">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500/30 border border-emerald-500/50 shadow-[0_0_8px_rgba(16,185,129,0.3)]" />
                      <span className="text-xs text-zinc-400">{language === 'de' ? 'Profit' : 'Profit'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-rose-500/30 border border-rose-500/50 shadow-[0_0_8px_rgba(244,63,94,0.3)]" />
                      <span className="text-xs text-zinc-400">{language === 'de' ? 'Verlust' : 'Loss'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-zinc-700/50 border border-zinc-600/50" />
                      <span className="text-xs text-zinc-400">{language === 'de' ? 'Keine Trades' : 'No trades'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        {/* CSV Import Modal */}
        <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle><FileSpreadsheet className="inline h-5 w-5 mr-2" />{t('importTrades')}</DialogTitle>
              <DialogDescription>{language === 'de' ? 'CSV oder XLSX wird automatisch analysiert und Spalten zugeordnet' : 'CSV or XLSX will be automatically analyzed and columns mapped'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Select value={selectedAccount || ''} onValueChange={setSelectedAccount}>
                <SelectTrigger data-testid="import-account-select"><SelectValue placeholder={t('selectAccount')} /></SelectTrigger>
                <SelectContent>{accounts.map((acc) => (<SelectItem key={acc.account_id} value={acc.account_id}>{acc.name}</SelectItem>))}</SelectContent>
              </Select>
              <div {...getCsvRootProps()} className={`dropzone text-center cursor-pointer ${isCsvDragActive ? 'dropzone-active' : ''}`} data-testid="csv-dropzone">
                <input {...getCsvInputProps()} />
                {importLoading ? <Loader2 className="h-8 w-8 mx-auto mb-2 text-primary animate-spin" /> : <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />}
                <p className="text-sm text-muted-foreground">{importLoading ? (language === 'de' ? 'Analysiere Datei...' : 'Analyzing file...') : (language === 'de' ? 'CSV oder XLSX Datei hierher ziehen' : 'Drag CSV or XLSX file here')}</p>
              </div>
              <Button variant="outline" onClick={() => setShowImportModal(false)} className="w-full">{t('cancel')}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* CSV Mapping Modal */}
        <Dialog open={showCsvMappingModal} onOpenChange={setShowCsvMappingModal}>
          <DialogContent className="glass max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-full bg-primary/20">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <DialogTitle>{language === 'de' ? 'CSV-Import vorbereiten' : 'Prepare CSV Import'}</DialogTitle>
                  <DialogDescription>{language === 'de' ? 'Wir haben Ihre Datei analysiert und die Spalten automatisch zugeordnet.' : 'We analyzed your file and mapped the columns automatically.'}</DialogDescription>
                </div>
              </div>
            </DialogHeader>
            {csvMapping && (
              <div className="space-y-4">
                {/* Confidence Badge */}
                <div className="flex items-center gap-2">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    (csvMapping.confidence || 0) >= 0.9 ? 'bg-emerald-500/20 text-emerald-400' :
                    (csvMapping.confidence || 0) >= 0.7 ? 'bg-amber-500/20 text-amber-400' :
                    'bg-rose-500/20 text-rose-400'
                  }`}>
                    {(csvMapping.confidence || 0) >= 0.9 ? '✓ ' : ''}
                    {Math.round((csvMapping.confidence || 0) * 100)}% {language === 'de' ? 'Erkennungsrate' : 'Detection Rate'}
                  </div>
                </div>

                {/* Mapping Table */}
                <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-zinc-700/50 border-b border-zinc-600">
                    <p className="text-sm font-medium">{language === 'de' ? 'Erkannte Felder' : 'Detected Fields'}</p>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(csvMapping.mapping || {}).map(([source, target]) => {
                        // Map technical field names to user-friendly German labels
                        const fieldLabels = {
                          'symbol': language === 'de' ? 'Symbol/Ticker' : 'Symbol/Ticker',
                          'buyPrice': language === 'de' ? 'Entry Preis' : 'Entry Price',
                          'sellPrice': language === 'de' ? 'Exit Preis' : 'Exit Price',
                          'qty': language === 'de' ? 'Anzahl/Menge' : 'Quantity',
                          'pnl': language === 'de' ? 'Gewinn/Verlust' : 'Profit/Loss',
                          'boughtTimestamp': language === 'de' ? 'Entry Zeitpunkt' : 'Entry Time',
                          'soldTimestamp': language === 'de' ? 'Exit Zeitpunkt' : 'Exit Time',
                          'duration': language === 'de' ? 'Dauer' : 'Duration'
                        };
                        const label = fieldLabels[target] || target;
                        return (
                          <div key={source} className="flex items-center gap-2 p-2 rounded bg-zinc-900/50">
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                            <span className="text-sm text-white truncate">{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {csvMapping.missing_targets?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-400 mb-1">
                          {language === 'de' ? 'Einige Felder konnten nicht zugeordnet werden' : 'Some fields could not be mapped'}
                        </p>
                        <p className="text-xs text-amber-400/70">
                          {language === 'de' ? 'Diese Felder fehlen in Ihrer CSV-Datei oder haben unbekannte Namen.' : 'These fields are missing from your CSV or have unknown names.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowCsvMappingModal(false)} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button onClick={handleCsvImport} disabled={importLoading} className="flex-1 glow-primary" data-testid="confirm-csv-import">
                    {importLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {t('importButton')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Screenshot Import Modal */}
        <Dialog open={showScreenshotModal} onOpenChange={setShowScreenshotModal}>
          <DialogContent className="glass max-w-2xl">
            <DialogHeader>
              <DialogTitle><Camera className="inline h-5 w-5 mr-2" />{language === 'de' ? 'Screenshot importieren' : 'Import Screenshot'}</DialogTitle>
              <DialogDescription>{language === 'de' ? 'Lade einen Screenshot deines Trades hoch. Optional: Schneide den relevanten Bereich zu.' : 'Upload a screenshot of your trade. Optional: Crop the relevant area.'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Select value={selectedAccount || ''} onValueChange={setSelectedAccount}>
                <SelectTrigger><SelectValue placeholder={t('selectAccount')} /></SelectTrigger>
                <SelectContent>{accounts.map((acc) => (<SelectItem key={acc.account_id} value={acc.account_id}>{acc.name}</SelectItem>))}</SelectContent>
              </Select>
              
              {!screenshotImage ? (
                <div {...getScreenshotRootProps()} className={`dropzone text-center cursor-pointer ${isScreenshotDragActive ? 'dropzone-active' : ''}`}>
                  <input {...getScreenshotInputProps()} />
                  <Camera className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">{language === 'de' ? 'Screenshot hierher ziehen oder klicken' : 'Drag screenshot here or click'}</p>
                </div>
              ) : showCropMode ? (
                <div className="space-y-3">
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <Crop className="h-4 w-4" />
                    {language === 'de' ? 'Ziehe einen Rahmen um den relevanten Bereich' : 'Draw a box around the relevant area'}
                  </p>
                  <div className="border border-zinc-700 rounded-lg overflow-hidden bg-zinc-950 max-h-[400px] overflow-y-auto">
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                    >
                      <img 
                        src={screenshotImage} 
                        alt="Screenshot" 
                        className="max-w-full crop-image-element"
                      />
                    </ReactCrop>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowCropMode(false)} className="flex-1">
                      {language === 'de' ? 'Abbrechen' : 'Cancel'}
                    </Button>
                    <Button onClick={handleCropComplete} className="flex-1">
                      <Check className="h-4 w-4 mr-2" />
                      {language === 'de' ? 'Zuschneiden' : 'Apply Crop'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative border border-zinc-700 rounded-lg overflow-hidden bg-zinc-950">
                    <img 
                      src={croppedImageUrl || screenshotImage} 
                      alt="Screenshot" 
                      className="max-h-[300px] mx-auto"
                    />
                    {croppedImageUrl && (
                      <div className="absolute top-2 right-2 bg-emerald-500/80 text-white text-xs px-2 py-1 rounded">
                        {language === 'de' ? 'Zugeschnitten' : 'Cropped'}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCropMode(true)}
                      className="flex-1"
                    >
                      <Crop className="h-4 w-4 mr-2" />
                      {language === 'de' ? 'Zuschneiden' : 'Crop'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setScreenshotImage(null);
                        setScreenshotBase64('');
                        setCroppedImageUrl(null);
                        setCrop(null);
                      }}
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-2" />
                      {language === 'de' ? 'Neues Bild' : 'New Image'}
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { 
                  setShowScreenshotModal(false); 
                  setScreenshotImage(null); 
                  setScreenshotBase64(''); 
                  setCroppedImageUrl(null);
                  setCrop(null);
                  setShowCropMode(false);
                }} className="flex-1">{t('cancel')}</Button>
                <Button 
                  onClick={handleScreenshotAnalyze} 
                  disabled={!screenshotBase64 || screenshotLoading || showCropMode} 
                  className="flex-1" 
                  data-testid="analyze-screenshot-btn"
                >
                  {screenshotLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
                  {language === 'de' ? 'KI Analysieren' : 'AI Analyze'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Manual Entry Modal (for incomplete screenshot analysis) */}
        <Dialog open={showManualEntryModal} onOpenChange={(open) => {
          setShowManualEntryModal(open);
          if (!open) {
            setShowSymbolSuggestions(false);
            setSymbolSuggestions([]);
          }
        }}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle>{language === 'de' ? 'Trade-Details ergänzen' : 'Complete Trade Details'}</DialogTitle>
              <DialogDescription>
                {screenshotAnalysis?.missing_fields?.length > 0 && (
                  <span className="text-amber-400">{language === 'de' ? 'Fehlende Felder' : 'Missing'}: {screenshotAnalysis.missing_fields.join(', ')}</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {/* Broker Info Banner */}
              {getCurrentAccountBroker && brokerSymbolData.symbols[getCurrentAccountBroker] && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
                  <Search className="h-4 w-4 text-emerald-400" />
                  <span className="text-emerald-400">
                    {brokerSymbolData.symbols[getCurrentAccountBroker].length} {language === 'de' ? 'Symbole verfügbar für' : 'symbols available for'}{' '}
                    <strong>{getOriginalBrokerName !== getCurrentAccountBroker ? `${getOriginalBrokerName} (${getCurrentAccountBroker})` : getCurrentAccountBroker}</strong>
                  </span>
                </div>
              )}
              {getOriginalBrokerName && !brokerSymbolData.symbols[getCurrentAccountBroker] && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span className="text-amber-400">
                    {language === 'de' 
                      ? `Keine Symbol-Datenbank für "${getOriginalBrokerName}" verfügbar. Manuelle Eingabe.`
                      : `No symbol database for "${getOriginalBrokerName}". Manual entry required.`}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {/* Symbol with Autocomplete */}
                <div className="relative">
                  <Label>Symbol <span className="text-rose-400">*</span></Label>
                  <Input 
                    value={manualTrade.symbol} 
                    onChange={(e) => handleSymbolInputChange(e.target.value)} 
                    onFocus={() => manualTrade.symbol && handleSymbolInputChange(manualTrade.symbol)}
                    onBlur={() => setTimeout(() => setShowSymbolSuggestions(false), 200)}
                    placeholder="z.B. MNQ" 
                    data-testid="manual-symbol"
                    autoComplete="off"
                    className={`placeholder:text-zinc-600 ${manualTrade.symbol && !isValidSymbol(manualTrade.symbol) ? 'border-red-500' : ''}`}
                  />
                  {/* Symbol Suggestions Dropdown with Tick Values */}
                  {showSymbolSuggestions && symbolSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 max-h-[250px] overflow-y-auto">
                      {symbolSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectSymbolSuggestion(suggestion)}
                          className="w-full px-3 py-2 text-left hover:bg-zinc-800 transition-colors border-b border-zinc-800 last:border-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono font-medium text-primary">{suggestion.s}</span>
                            <span className="text-xs text-muted-foreground truncate ml-2 max-w-[120px]">{suggestion.n}</span>
                          </div>
                          {(suggestion.t || suggestion.v || suggestion.c) && (
                            <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                              {suggestion.t && <span>Tick: <span className="text-emerald-400">{suggestion.t}</span></span>}
                              {suggestion.v && <span>Wert: <span className="text-emerald-400">{suggestion.v}</span></span>}
                              {suggestion.c && <span>Größe: <span className="text-amber-400">{suggestion.c}</span></span>}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>{language === 'de' ? 'Menge' : 'Quantity'} <span className="text-rose-400">*</span></Label>
                  <Input 
                    type="number" 
                    value={manualTrade.qty} 
                    onChange={(e) => setManualTrade({...manualTrade, qty: e.target.value})} 
                    placeholder="z.B. 1" 
                    data-testid="manual-qty" 
                    className={`placeholder:text-zinc-600 ${hasFieldError('qty', manualTrade.qty) ? 'border-red-500' : ''}`}
                  />
                  {hasFieldError('qty', manualTrade.qty) && (
                    <p className="text-xs text-red-400 mt-1">{getFieldError('qty', manualTrade.qty)}</p>
                  )}
                </div>
                <div>
                  <Label>{language === 'de' ? 'Entry' : 'Entry'} <span className="text-rose-400">*</span></Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={manualTrade.buy_price} 
                    onChange={(e) => setManualTrade({...manualTrade, buy_price: e.target.value})} 
                    placeholder="z.B. 25860.25" 
                    data-testid="manual-buy-price" 
                    className={`placeholder:text-zinc-600 ${hasFieldError('price', manualTrade.buy_price) ? 'border-red-500' : ''}`}
                  />
                  {hasFieldError('price', manualTrade.buy_price) && (
                    <p className="text-xs text-red-400 mt-1">{getFieldError('price', manualTrade.buy_price)}</p>
                  )}
                </div>
                <div>
                  <Label>{language === 'de' ? 'Exit' : 'Exit'} <span className="text-rose-400">*</span></Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={manualTrade.sell_price} 
                    onChange={(e) => setManualTrade({...manualTrade, sell_price: e.target.value})} 
                    placeholder="z.B. 25916.25" 
                    data-testid="manual-sell-price" 
                    className={`placeholder:text-zinc-600 ${hasFieldError('price', manualTrade.sell_price) ? 'border-red-500' : ''}`}
                  />
                  {hasFieldError('price', manualTrade.sell_price) && (
                    <p className="text-xs text-red-400 mt-1">{getFieldError('price', manualTrade.sell_price)}</p>
                  )}
                </div>
              </div>
              
              {/* Direction Selection */}
              <div>
                <Label>{language === 'de' ? 'Richtung' : 'Direction'}</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setManualTrade({...manualTrade, direction: 'long'})}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                      manualTrade.direction === 'long' 
                        ? 'bg-emerald-500/20 text-emerald-400 border-2 border-emerald-500' 
                        : 'bg-zinc-800 text-muted-foreground border border-zinc-700 hover:bg-zinc-700'
                    }`}
                    data-testid="direction-long"
                  >
                    📈 Long
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualTrade({...manualTrade, direction: 'short'})}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                      manualTrade.direction === 'short' 
                        ? 'bg-rose-500/20 text-rose-400 border-2 border-rose-500' 
                        : 'bg-zinc-800 text-muted-foreground border border-zinc-700 hover:bg-zinc-700'
                    }`}
                    data-testid="direction-short"
                  >
                    📉 Short
                  </button>
                </div>
              </div>
              
              {/* Date/Time with Auto-Formatting */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'de' ? 'Entry Datum & Zeit' : 'Entry Date & Time'} <span className="text-rose-400">*</span></Label>
                  <Input 
                    value={manualTrade.bought_timestamp} 
                    onChange={(e) => handleDateTimeInput('bought_timestamp', e.target.value)} 
                    placeholder="z.B. 02.02.2026 16:30:00" 
                    data-testid="manual-buy-time"
                    className={`font-mono placeholder:text-zinc-600 ${getTimestampError(manualTrade.bought_timestamp) ? 'border-red-500' : ''}`}
                  />
                  {getTimestampError(manualTrade.bought_timestamp) ? (
                    <p className="text-xs text-red-400 mt-1">{getTimestampError(manualTrade.bought_timestamp)}</p>
                  ) : (
                    <p className="text-xs text-zinc-600 mt-1">TT.MM.JJJJ HH:MM:SS</p>
                  )}
                </div>
                <div>
                  <Label>{language === 'de' ? 'Exit Datum & Zeit' : 'Exit Date & Time'} <span className="text-rose-400">*</span></Label>
                  <Input 
                    value={manualTrade.sold_timestamp} 
                    onChange={(e) => handleDateTimeInput('sold_timestamp', e.target.value)} 
                    placeholder="z.B. 02.02.2026 16:45:00" 
                    data-testid="manual-sell-time"
                    className={`font-mono placeholder:text-zinc-600 ${getTimestampError(manualTrade.sold_timestamp) ? 'border-red-500' : ''}`}
                  />
                  {getTimestampError(manualTrade.sold_timestamp) ? (
                    <p className="text-xs text-red-400 mt-1">{getTimestampError(manualTrade.sold_timestamp)}</p>
                  ) : (
                    <p className="text-xs text-zinc-600 mt-1">TT.MM.JJJJ HH:MM:SS</p>
                  )}
                </div>
              </div>
              
              {/* Strategy Selection - Always show */}
              <div>
                <Label>{language === 'de' ? 'Strategie' : 'Strategy'}</Label>
                <select 
                  value={manualTrade.strategy_id} 
                  onChange={(e) => setManualTrade({...manualTrade, strategy_id: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="manual-strategy"
                >
                  <option value="">{language === 'de' ? 'Keine Strategie' : 'No strategy'}</option>
                  {strategies.map((s) => (
                    <option key={s.strategy_id} value={s.strategy_id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {strategies.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {language === 'de' ? 'Erstelle Strategien im Strategien-Bereich' : 'Create strategies in the Strategies section'}
                  </p>
                )}
              </div>
              
              <div><Label>{language === 'de' ? 'Notizen' : 'Notes'}</Label><Textarea value={manualTrade.notes} onChange={(e) => setManualTrade({...manualTrade, notes: e.target.value})} placeholder={language === 'de' ? 'Optionale Notizen...' : 'Optional notes...'} /></div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowManualEntryModal(false)} className="flex-1">{t('cancel')}</Button>
                <Button 
                  onClick={() => handleCreateTradeFromScreenshot()} 
                  disabled={importLoading || !manualTrade.symbol || !manualTrade.buy_price || !manualTrade.sell_price || !manualTrade.qty || !isValidTimestamp(manualTrade.bought_timestamp) || !isValidTimestamp(manualTrade.sold_timestamp)} 
                  className="flex-1" 
                  data-testid="save-manual-trade"
                >
                  {importLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {t('save')}
                </Button>
              </div>
              {/* Validation hint */}
              {(!isValidTimestamp(manualTrade.bought_timestamp) || !isValidTimestamp(manualTrade.sold_timestamp)) && manualTrade.bought_timestamp && manualTrade.sold_timestamp && (
                <p className="text-xs text-red-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {language === 'de' ? 'Bitte korrigiere die ungültigen Zeitangaben' : 'Please correct the invalid timestamps'}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Day Journal Modal */}
        <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
          <DialogContent className="glass max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${getCheckInIndicator(checkInData).bg}`} />
                {formatDateDisplay(selectedDate)}
              </DialogTitle>
              <DialogDescription>
                {dayJournal?.trade_count > 0 && (
                  <span className={dayJournal.total_pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                    {dayJournal.trade_count} Trades • {dayJournal.total_pnl >= 0 ? '+' : ''}{dayJournal.total_pnl?.toFixed(2)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              {/* Mood Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">{language === 'de' ? 'Stimmung' : 'Mood'}</Label>
                  <span className="text-sm font-mono text-primary">{checkInData.mood}/10</span>
                </div>
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  value={checkInData.mood}
                  onChange={(e) => setCheckInData(prev => ({ ...prev, mood: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-zinc-800 rounded-full appearance-none cursor-pointer accent-emerald-500"
                  data-testid="mood-slider"
                />
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>{language === 'de' ? 'Schlecht' : 'Bad'}</span>
                  <span>{language === 'de' ? 'Ausgezeichnet' : 'Excellent'}</span>
                </div>
              </div>

              {/* Energy Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">{language === 'de' ? 'Energie-Level' : 'Energy Level'}</Label>
                  <span className="text-sm font-mono text-primary">{checkInData.energy}/10</span>
                </div>
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  value={checkInData.energy}
                  onChange={(e) => setCheckInData(prev => ({ ...prev, energy: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-zinc-800 rounded-full appearance-none cursor-pointer accent-emerald-500"
                  data-testid="energy-slider"
                />
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>{language === 'de' ? 'Müde' : 'Tired'}</span>
                  <span>{language === 'de' ? 'Energiegeladen' : 'Energized'}</span>
                </div>
              </div>

              {/* Stress Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">{language === 'de' ? 'Stress-Level' : 'Stress Level'}</Label>
                  <span className="text-sm font-mono text-primary">{checkInData.stress}/10</span>
                </div>
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  value={checkInData.stress}
                  onChange={(e) => setCheckInData(prev => ({ ...prev, stress: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-zinc-800 rounded-full appearance-none cursor-pointer accent-rose-500"
                  data-testid="stress-slider"
                />
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>{language === 'de' ? 'Entspannt' : 'Relaxed'}</span>
                  <span>{language === 'de' ? 'Gestresst' : 'Stressed'}</span>
                </div>
              </div>

              {/* Sleep Quality */}
              <div>
                <Label className="text-sm font-medium mb-2 block">{language === 'de' ? 'Schlafqualität' : 'Sleep Quality'}</Label>
                <div className="flex gap-2">
                  {[
                    { value: 'good', label: language === 'de' ? 'Gut' : 'Good', color: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400' },
                    { value: 'okay', label: 'Okay', color: 'bg-amber-500/20 border-amber-500/50 text-amber-400' },
                    { value: 'bad', label: language === 'de' ? 'Schlecht' : 'Bad', color: 'bg-rose-500/20 border-rose-500/50 text-rose-400' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setCheckInData(prev => ({ ...prev, sleepQuality: option.value }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                        checkInData.sleepQuality === option.value 
                          ? option.color 
                          : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700'
                      }`}
                      data-testid={`sleep-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Day Notes */}
              <div>
                <Label className="text-sm font-medium mb-2 block">{language === 'de' ? 'Tages-Notizen' : 'Day Notes'}</Label>
                <Textarea 
                  value={dayNotes} 
                  onChange={(e) => setDayNotes(e.target.value)} 
                  placeholder={language === 'de' ? 'Was hast du heute gelernt? Erkenntnisse, Fehler, Erfolge...' : 'What did you learn today?'} 
                  className="min-h-[80px] bg-zinc-800/50" 
                  data-testid="day-notes-input" 
                />
              </div>

              {/* Screenshots */}
              <div>
                <Label className="mb-2 block">{language === 'de' ? 'Screenshots' : 'Screenshots'}</Label>
                <div className="space-y-2">
                  {dayScreenshots.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {dayScreenshots.map((screenshot, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={screenshot} 
                            alt={`Screenshot ${index + 1}`} 
                            className="w-full h-24 object-cover rounded-lg border border-zinc-700"
                          />
                          <button
                            onClick={() => removeDayScreenshot(index)}
                            className="absolute top-1 right-1 p-1 bg-rose-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-zinc-700 rounded-lg hover:border-primary cursor-pointer transition-colors bg-zinc-800/50">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleDayScreenshotUpload}
                      className="hidden"
                      data-testid="screenshot-upload-input"
                    />
                    <Image className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {language === 'de' ? 'Screenshot hinzufügen' : 'Add Screenshot'}
                    </span>
                  </label>
                </div>
              </div>
              {dayJournal?.trades?.length > 0 && (
                <div>
                  <Label className="mb-3 block flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    {language === 'de' ? 'Trades an diesem Tag' : 'Trades on this day'}
                  </Label>
                  <div className="max-h-[280px] overflow-y-auto space-y-3 pr-1">
                    {dayJournal.trades.map((trade, i) => {
                      const isProfit = trade.pnl >= 0;
                      const strategy = strategies.find(s => s.strategy_id === trade.strategy_id);
                      
                      return (
                        <div 
                          key={trade.trade_id || i} 
                          className={`
                            relative rounded-xl p-4 transition-all duration-300
                            ${isProfit 
                              ? 'bg-gradient-to-br from-emerald-500/10 via-zinc-900/80 to-zinc-900/80 border border-emerald-500/20' 
                              : 'bg-gradient-to-br from-rose-500/10 via-zinc-900/80 to-zinc-900/80 border border-rose-500/20'
                            }
                            hover:scale-[1.02] hover:shadow-lg
                            ${isProfit ? 'hover:shadow-emerald-500/10' : 'hover:shadow-rose-500/10'}
                          `}
                        >
                          {/* Header with Symbol and P&L */}
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`
                                w-10 h-10 rounded-lg flex items-center justify-center
                                ${isProfit ? 'bg-emerald-500/20' : 'bg-rose-500/20'}
                              `}>
                                <TrendingUp className={`h-5 w-5 ${isProfit ? 'text-emerald-400' : 'text-rose-400 rotate-180'}`} />
                              </div>
                              <div>
                                <span className="font-bold text-base">{trade.symbol}</span>
                                {trade.direction && (
                                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                    trade.direction === 'long' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
                                  }`}>
                                    {trade.direction.toUpperCase()}
                                  </span>
                                )}
                                {strategy && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: strategy.color }} />
                                    <span className="text-xs text-muted-foreground">{strategy.name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* P&L Badge */}
                            <div className={`
                              px-3 py-1.5 rounded-lg font-mono font-bold text-sm
                              ${isProfit 
                                ? 'bg-emerald-500/20 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.2)]' 
                                : 'bg-rose-500/20 text-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.2)]'
                              }
                            `}>
                              {isProfit ? '+' : ''}{trade.pnl?.toFixed(2)}
                            </div>
                          </div>
                          
                          {/* Trade Details */}
                          <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                              <p className="text-muted-foreground mb-0.5">{language === 'de' ? 'Einstieg' : 'Entry'}</p>
                              <p className="font-mono font-medium">{trade.buy_price?.toFixed(2) || '-'}</p>
                            </div>
                            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                              <p className="text-muted-foreground mb-0.5">{language === 'de' ? 'Ausstieg' : 'Exit'}</p>
                              <p className="font-mono font-medium">{trade.sell_price?.toFixed(2) || '-'}</p>
                            </div>
                            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                              <p className="text-muted-foreground mb-0.5">{language === 'de' ? 'Menge' : 'Qty'}</p>
                              <p className="font-mono font-medium">{trade.qty || '-'}</p>
                            </div>
                          </div>
                          
                          {/* Screenshot Preview */}
                          {hasValidScreenshot(trade) && (
                            <div 
                              className="relative group cursor-pointer mb-3 rounded-lg overflow-hidden" 
                              onClick={() => window.open(getScreenshotUrl(trade), '_blank')}
                            >
                              <img 
                                src={getScreenshotUrl(trade)} 
                                alt={`${trade.symbol} Screenshot`}
                                className="w-full h-24 object-cover opacity-80 hover:opacity-100 transition-all duration-300"
                              />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/60 to-transparent">
                                <span className="text-xs text-white bg-black/50 px-3 py-1.5 rounded-full flex items-center gap-1">
                                  <Image className="h-3 w-3" />
                                  {language === 'de' ? 'Vergrößern' : 'Enlarge'}
                                </span>
                              </div>
                            </div>
                          )}
                          {/* Invalid screenshot warning - only show if there's a screenshot_url that's invalid */}
                          {trade.screenshot_url && !hasValidScreenshot(trade) && (
                            <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                              <p className="text-xs text-amber-400 flex items-center gap-1.5">
                                <AlertTriangle className="h-3 w-3" />
                                {language === 'de' ? 'Screenshot nicht mehr verfügbar' : 'Screenshot no longer available'}
                              </p>
                            </div>
                          )}
                          
                          {/* Actions & Strategy Selector */}
                          <div className="flex items-center justify-between pt-2 border-t border-zinc-700/50">
                            {strategies.length > 0 && (
                              <Select 
                                value={trade.strategy_id || 'none'} 
                                onValueChange={(value) => handleUpdateTradeStrategy(trade.trade_id, value === 'none' ? null : value)}
                              >
                                <SelectTrigger className="h-8 w-[140px] text-xs bg-zinc-800/50 border-zinc-700">
                                  <div className="flex items-center gap-1.5">
                                    <Target className="h-3 w-3 text-muted-foreground" />
                                    <SelectValue placeholder={language === 'de' ? 'Strategie' : 'Strategy'} />
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    <span className="text-muted-foreground">{language === 'de' ? 'Keine' : 'None'}</span>
                                  </SelectItem>
                                  {strategies.map((s) => (
                                    <SelectItem key={s.strategy_id} value={s.strategy_id}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                                        {s.name}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            
                            <div className="flex items-center gap-1">
                              {hasValidScreenshot(trade) && (
                                <button
                                  onClick={() => window.open(getScreenshotUrl(trade), '_blank')}
                                  className="p-2 hover:bg-zinc-700/50 rounded-lg transition-colors"
                                  title={language === 'de' ? 'Screenshot' : 'Screenshot'}
                                >
                                  <Image className="h-4 w-4 text-emerald-400" />
                                </button>
                              )}
                              <button 
                                onClick={() => handleEditTrade(trade)}
                                className="p-2 hover:bg-zinc-700/50 rounded-lg transition-colors"
                                title={language === 'de' ? 'Bearbeiten' : 'Edit'}
                                data-testid={`edit-trade-${trade.trade_id}`}
                              >
                                <PenLine className="h-4 w-4 text-muted-foreground hover:text-emerald-400" />
                              </button>
                              <button 
                                onClick={() => handleDeleteTrade(trade)}
                                disabled={deleteTradeLoading === trade.trade_id}
                                className="p-2 hover:bg-rose-500/20 rounded-lg transition-colors"
                                title={language === 'de' ? 'Löschen' : 'Delete'}
                                data-testid={`delete-trade-${trade.trade_id}`}
                              >
                                {deleteTradeLoading === trade.trade_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-rose-400" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button onClick={handleSaveDayJournal} className="w-full" data-testid="save-day-journal">{t('save')}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Trade Modal */}
        <Dialog open={showEditTradeModal} onOpenChange={setShowEditTradeModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle>{language === 'de' ? 'Trade bearbeiten' : 'Edit Trade'}</DialogTitle>
              <DialogDescription>
                {language === 'de' ? 'Bearbeite die Details dieses Trades' : 'Edit the details of this trade'}
              </DialogDescription>
            </DialogHeader>
            {editingTrade && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Symbol' : 'Symbol'} <span className="text-rose-400">*</span></Label>
                    <Input
                      value={editingTrade.symbol || ''}
                      onChange={(e) => setEditingTrade({...editingTrade, symbol: e.target.value})}
                      data-testid="edit-trade-symbol"
                      placeholder="z.B. MNQ"
                      className={`placeholder:text-zinc-600 ${editingTrade.symbol && !isValidSymbol(editingTrade.symbol) ? 'border-red-500' : ''}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Menge' : 'Quantity'} <span className="text-rose-400">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingTrade.qty || ''}
                      onChange={(e) => setEditingTrade({...editingTrade, qty: e.target.value})}
                      data-testid="edit-trade-qty"
                      placeholder="z.B. 1"
                      className={`placeholder:text-zinc-600 ${hasFieldError('qty', editingTrade.qty) ? 'border-red-500' : ''}`}
                    />
                    {hasFieldError('qty', editingTrade.qty) && (
                      <p className="text-xs text-red-400">{getFieldError('qty', editingTrade.qty)}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Entry Preis' : 'Entry Price'} <span className="text-rose-400">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingTrade.buy_price || ''}
                      onChange={(e) => setEditingTrade({...editingTrade, buy_price: e.target.value})}
                      data-testid="edit-trade-entry"
                      placeholder="z.B. 25860.25"
                      className={`placeholder:text-zinc-600 ${hasFieldError('price', editingTrade.buy_price) ? 'border-red-500' : ''}`}
                    />
                    {hasFieldError('price', editingTrade.buy_price) && (
                      <p className="text-xs text-red-400">{getFieldError('price', editingTrade.buy_price)}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Exit Preis' : 'Exit Price'} <span className="text-rose-400">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingTrade.sell_price || ''}
                      onChange={(e) => setEditingTrade({...editingTrade, sell_price: e.target.value})}
                      data-testid="edit-trade-exit"
                      placeholder="z.B. 25916.25"
                      className={`placeholder:text-zinc-600 ${hasFieldError('price', editingTrade.sell_price) ? 'border-red-500' : ''}`}
                    />
                    {hasFieldError('price', editingTrade.sell_price) && (
                      <p className="text-xs text-red-400">{getFieldError('price', editingTrade.sell_price)}</p>
                    )}
                  </div>
                </div>
                
                {/* Direction Selection */}
                <div className="space-y-2">
                  <Label>{language === 'de' ? 'Richtung' : 'Direction'}</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingTrade({...editingTrade, direction: 'long'})}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        editingTrade.direction === 'long' 
                          ? 'bg-emerald-500/20 text-emerald-400 border-2 border-emerald-500' 
                          : 'bg-zinc-800 text-muted-foreground border border-zinc-700 hover:bg-zinc-700'
                      }`}
                      data-testid="edit-direction-long"
                    >
                      📈 Long
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingTrade({...editingTrade, direction: 'short'})}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        editingTrade.direction === 'short' 
                          ? 'bg-rose-500/20 text-rose-400 border-2 border-rose-500' 
                          : 'bg-zinc-800 text-muted-foreground border border-zinc-700 hover:bg-zinc-700'
                      }`}
                      data-testid="edit-direction-short"
                    >
                      📉 Short
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Entry Datum' : 'Entry Date'}</Label>
                    <Input
                      type="datetime-local"
                      value={editingTrade.bought_timestamp || ''}
                      onChange={(e) => setEditingTrade({...editingTrade, bought_timestamp: e.target.value})}
                      data-testid="edit-trade-entry-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Exit Datum' : 'Exit Date'}</Label>
                    <Input
                      type="datetime-local"
                      value={editingTrade.sold_timestamp || ''}
                      onChange={(e) => setEditingTrade({...editingTrade, sold_timestamp: e.target.value})}
                      data-testid="edit-trade-exit-date"
                    />
                  </div>
                </div>
                {strategies.length > 0 && (
                  <div className="space-y-2">
                    <Label>{language === 'de' ? 'Strategie' : 'Strategy'}</Label>
                    <Select 
                      value={editingTrade.strategy_id || 'none'} 
                      onValueChange={(value) => setEditingTrade({...editingTrade, strategy_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger data-testid="edit-trade-strategy">
                        <SelectValue placeholder={language === 'de' ? 'Strategie wählen' : 'Select strategy'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-muted-foreground">{language === 'de' ? 'Keine Strategie' : 'No strategy'}</span>
                        </SelectItem>
                        {strategies.map((s) => (
                          <SelectItem key={s.strategy_id} value={s.strategy_id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                              {s.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{language === 'de' ? 'Notizen' : 'Notes'}</Label>
                  <Textarea
                    value={editingTrade.notes || ''}
                    onChange={(e) => setEditingTrade({...editingTrade, notes: e.target.value})}
                    rows={3}
                    placeholder={language === 'de' ? 'Optionale Notizen zum Trade...' : 'Optional notes about the trade...'}
                    data-testid="edit-trade-notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {setShowEditTradeModal(false); setEditingTrade(null);}}
                data-testid="edit-trade-cancel"
              >
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleSaveEditedTrade} 
                disabled={editTradeLoading}
                data-testid="edit-trade-save"
              >
                {editTradeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {language === 'de' ? 'Speichern' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
          <DialogContent className="glass max-w-sm">
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-rose-400" />
                </div>
              </div>
              <DialogTitle className="text-center">{language === 'de' ? 'Trade löschen?' : 'Delete Trade?'}</DialogTitle>
              <DialogDescription className="text-center">
                {tradeToDelete && (
                  <span>
                    {language === 'de' 
                      ? `Möchtest du den Trade "${tradeToDelete.symbol}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
                      : `Do you really want to delete the trade "${tradeToDelete.symbol}"? This action cannot be undone.`
                    }
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button 
                variant="outline" 
                onClick={() => {setShowDeleteConfirmModal(false); setTradeToDelete(null);}}
                className="flex-1"
                data-testid="delete-trade-cancel"
              >
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button 
                variant="destructive"
                onClick={confirmDeleteTrade} 
                disabled={deleteTradeLoading}
                className="flex-1"
                data-testid="delete-trade-confirm"
              >
                {deleteTradeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                {language === 'de' ? 'Löschen' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Success Modal */}
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent className="glass max-w-sm">
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              </div>
              <DialogTitle className="text-xl mb-2">{language === 'de' ? 'Import abgeschlossen!' : 'Import Complete!'}</DialogTitle>
              
              {/* Import Summary */}
              <div className="space-y-3 mt-4">
                {/* Imported trades */}
                <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm">{language === 'de' ? 'Importiert' : 'Imported'}</span>
                  </div>
                  <span className="text-lg font-bold text-emerald-400">{importResult?.imported || 0}</span>
                </div>
                
                {/* Skipped duplicates - only show if > 0 */}
                {(importResult?.skipped_duplicates || 0) > 0 && (
                  <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-400" />
                      <span className="text-sm">{language === 'de' ? 'Übersprungen (Duplikate)' : 'Skipped (Duplicates)'}</span>
                    </div>
                    <span className="text-lg font-bold text-amber-400">{importResult?.skipped_duplicates || 0}</span>
                  </div>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground mt-4">
                {language === 'de' 
                  ? 'Ihre Statistiken wurden automatisch aktualisiert.'
                  : 'Your statistics have been automatically updated.'}
              </p>
              <Button onClick={() => setShowSuccessModal(false)} className="mt-4 w-full glow-primary" data-testid="success-close-btn">{language === 'de' ? 'Schließen' : 'Close'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Trade Modal (3 Options) */}
        <Dialog open={showAddTradeModal} onOpenChange={(open) => { setShowAddTradeModal(open); if (!open) setPreselectedTradeDate(null); }}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                {language === 'de' ? 'Trade hinzufügen' : 'Add Trade'}
              </DialogTitle>
              <DialogDescription>
                {preselectedTradeDate ? (
                  language === 'de'
                    ? `Für den ${new Date(preselectedTradeDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}. Datum wird vorbelegt.`
                    : `For ${new Date(preselectedTradeDate).toLocaleDateString('en-US')}. Date will be prefilled.`
                ) : (
                  language === 'de' ? 'Wähle, wie du deinen Trade erfassen möchtest.' : 'Choose how you want to record your trade.'
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-4">
              {/* Manual Entry */}
              <button
                onClick={() => {
                  setShowAddTradeModal(false);
                  let prefilledEntry = '';
                  let prefilledExit = '';
                  if (preselectedTradeDate) {
                    const [y, m, d] = preselectedTradeDate.split('-');
                    prefilledEntry = `${d}.${m}.${y} 09:00:00`;
                    prefilledExit = `${d}.${m}.${y} 10:00:00`;
                  }
                  setManualTrade({ symbol: '', buy_price: '', sell_price: '', qty: '', bought_timestamp: prefilledEntry, sold_timestamp: prefilledExit, notes: '', strategy_id: '', direction: 'long' });
                  setShowManualEntryModal(true);
                  setPreselectedTradeDate(null);
                }}
                className="flex items-center gap-4 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800 hover:border-zinc-700 transition-all group"
                data-testid="add-trade-manual"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                  <PenLine className="h-6 w-6 text-emerald-400" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{language === 'de' ? 'Manueller Eintrag' : 'Manual Entry'}</p>
                  <p className="text-sm text-muted-foreground">{language === 'de' ? 'Trade-Daten selbst eingeben' : 'Enter trade data yourself'}</p>
                </div>
              </button>
              
              {/* Screenshot - Shows Pro upgrade modal for Basic users */}
              <button
                onClick={() => {
                  if (user?.subscription_plan === 'pro' || user?.subscription_plan === 'premium') {
                    setShowAddTradeModal(false);
                    setScreenshotImage(null);
                    setScreenshotBase64('');
                    setScreenshotAnalysis(null);
                    setShowScreenshotModal(true);
                  } else {
                    setShowAddTradeModal(false);
                    setShowProUpgradeModal(true);
                  }
                }}
                className="flex items-center gap-4 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800 hover:border-zinc-700 transition-all group"
                data-testid="add-trade-screenshot"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <Camera className="h-6 w-6 text-purple-400" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-medium">{language === 'de' ? 'Screenshot Upload' : 'Screenshot Upload'}</p>
                  <p className="text-sm text-muted-foreground">{language === 'de' ? 'KI erkennt Trade-Daten automatisch' : 'AI detects trade data automatically'}</p>
                </div>
                {user?.subscription_plan === 'basic' && (
                  <div className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                    <span className="text-[10px] font-bold text-emerald-400">PRO</span>
                  </div>
                )}
              </button>
              
              {/* CSV Import */}
              <button
                onClick={() => {
                  setShowAddTradeModal(false);
                  setShowImportModal(true);
                }}
                className="flex items-center gap-4 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800 hover:border-zinc-700 transition-all group"
                data-testid="add-trade-csv"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                  <FileSpreadsheet className="h-6 w-6 text-emerald-400" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{language === 'de' ? 'CSV / XLSX Import' : 'CSV / XLSX Import'}</p>
                  <p className="text-sm text-muted-foreground">{language === 'de' ? 'Trades per CSV oder Excel importieren' : 'Import trades via CSV or Excel'}</p>
                </div>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Account Modal */}
        <Dialog open={showCreateAccountModal} onOpenChange={setShowCreateAccountModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                {language === 'de' ? 'Neues Konto erstellen' : 'Create New Account'}
              </DialogTitle>
              <DialogDescription>
                {language === 'de' ? 'Erstelle ein neues Trading-Konto für deine Trades.' : 'Create a new trading account for your trades.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAccount} className="space-y-4 py-4">
              {accountError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {accountError}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="accountName">{language === 'de' ? 'Kontoname' : 'Account Name'}</Label>
                <Input
                  id="accountName"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  required
                  placeholder={language === 'de' ? 'z.B. Hauptkonto' : 'e.g. Main Account'}
                  data-testid="new-account-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'de' ? 'Broker' : 'Broker'}</Label>
                <Select value={newAccountBroker} onValueChange={setNewAccountBroker}>
                  <SelectTrigger data-testid="new-account-broker-select">
                    <SelectValue placeholder={language === 'de' ? 'Broker auswählen...' : 'Select broker...'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[250px]">
                    {BROKERS.map((broker) => (
                      <SelectItem key={broker.id} value={broker.id}>
                        {broker.name} {broker.country && <span className="text-muted-foreground text-xs">({broker.country})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'de' ? 'Währung' : 'Currency'}</Label>
                <Select value={newAccountCurrency} onValueChange={setNewAccountCurrency}>
                  <SelectTrigger data-testid="new-account-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateAccountModal(false)}>
                  {language === 'de' ? 'Abbrechen' : 'Cancel'}
                </Button>
                <Button type="submit" disabled={accountCreationLoading} data-testid="create-account-submit">
                  {accountCreationLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  {language === 'de' ? 'Erstellen' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Premium Upgrade Modal */}
        <PremiumUpgradeModal 
          open={showUpgradeModal} 
          onOpenChange={setShowUpgradeModal}
          type="account_limit"
        />

        {/* Pro Feature Upgrade Modal (Screenshot) */}
        <PremiumUpgradeModal 
          open={showProUpgradeModal} 
          onOpenChange={setShowProUpgradeModal}
          type="pro_screenshot"
        />

        {/* Delete Account Confirmation Modal */}
        <Dialog open={showDeleteAccountModal} onOpenChange={setShowDeleteAccountModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-400">
                <Trash2 className="h-5 w-5" />
                {language === 'de' ? 'Konto löschen?' : 'Delete Account?'}
              </DialogTitle>
              <DialogDescription>
                {language === 'de' 
                  ? `Möchtest du das Konto "${accountToDelete?.name}" wirklich löschen? Alle damit verbundenen Trades werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`
                  : `Do you really want to delete the account "${accountToDelete?.name}"? All associated trades will also be deleted. This action cannot be undone.`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setAccountToDelete(null);
                }}
                data-testid="cancel-delete-account"
              >
                {language === 'de' ? 'Abbrechen' : 'Cancel'}
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                data-testid="confirm-delete-account"
              >
                {deleteLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                {language === 'de' ? 'Löschen' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Export Modal */}
        <PDFExportModal
          open={showExportModal}
          onOpenChange={setShowExportModal}
          accounts={accounts}
          selectedAccountId={selectedAccount}
        />
      </div>
    </TooltipProvider>
  );
};

export default DashboardPage;
